import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../lib/toast';
import { parseMarkdown } from '../lib/markdown';
import Sidebar from '../components/layout/Sidebar';
import {
  Plus,
  Minus,
  Trash2,
  Search,
  X,
  Users,
  Utensils,
  Phone,
  MapPin,
  Clock,
  Package,
  Truck,
  RefreshCw,
  ChefHat,
  DollarSign,
  FileText,
  User,
  Home,
  Hash,
  Percent,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Split,
  UserPlus,
  Layers,
  Check,
  Star,
  Info,
  Store,
  CheckCircle,
  TrendingUp,
  History,
  ArrowRightLeft,
  ShoppingCart,
  CreditCard,
  Pencil,
  MessageSquare,
  Gift
} from 'lucide-react';

// Import modular POS components
import {
  WeightModal,
  CancelExistingItemModal,
  VoidOrderModal,
  DiscountModal,
  ChangeTableModal,
  EditCustomerModal,
  DuplicateCustomerModal,
  NotesModal,
  RedeemModal
} from '../components/pos/POSModals';
import CloseTableModal from '../components/pos/CloseTableModal';
import CustomProductModal from '../components/pos/CustomProductModal';
import { VIPAlertModal, LoyaltyPanel } from '../components/pos/LoyaltyModals';

// Use environment variable for preview, or current origin for production
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const hostname = window.location.hostname || '';
  if (hostname.includes('emergent.host') || hostname.includes('emergent-')) {
    return window.location.origin;
  }
  return envUrl || window.location.origin;
};
const API = getApiUrl();

const ORDER_STATUSES = {
  pending: { label: 'Pendiente', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
  preparing: { label: 'En Preparación', color: 'bg-blue-500', textColor: 'text-blue-600' },
  ready: { label: 'Listo', color: 'bg-green-500', textColor: 'text-green-600' },
  sent: { label: 'Enviado', color: 'bg-purple-500', textColor: 'text-purple-600' },
  completed: { label: 'Cerrada', color: 'bg-gray-500', textColor: 'text-gray-600' },
  cancelled: { label: 'Cancelado', color: 'bg-red-500', textColor: 'text-red-600' }
};

const POS = () => {
  const { api, user } = useAuth();
  const { branchId, branchName, isMatriz } = useBranch();
  
  // Role-based permissions
  const userRole = user?.role || 'cajero';
  const isCajera = userRole === 'cajero' || userRole === 'cajera';
  const isAdmin = userRole === 'admin' || userRole === 'gerente' || userRole === 'administrador';
  const canEditOrders = isAdmin; // Solo admin puede editar órdenes
  const canVoidOrders = isAdmin; // Solo admin puede anular órdenes
  const canApplyDiscounts = isAdmin; // Solo admin puede aplicar descuentos
  const canSeeReports = isAdmin; // Solo admin puede ver reportes detallados
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [orderSearchQuery, setOrderSearchQuery] = useState(''); // Separate search for orders list
  const [selectedTable, setSelectedTable] = useState(null);
  const [orderType, setOrderType] = useState('dine_in');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showNewOrderPanel, setShowNewOrderPanel] = useState(false);
  
  // Ref to force panel to stay open after order creation
  const keepPanelOpenRef = useRef(false);
  
  // Product Notes Modal (Quick Notes)
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesProduct, setNotesProduct] = useState(null);
  const [productNotes, setProductNotes] = useState('');
  const [selectedQuickNotes, setSelectedQuickNotes] = useState([]);
  
  // Predefined quick notes
  const quickNotes = [
    { id: 1, label: 'Sin cebolla', category: 'sin' },
    { id: 2, label: 'Sin cilantro', category: 'sin' },
    { id: 3, label: 'Sin picante', category: 'sin' },
    { id: 4, label: 'Sin sal', category: 'sin' },
    { id: 5, label: 'Sin salsa', category: 'sin' },
    { id: 6, label: 'Extra queso', category: 'extra' },
    { id: 7, label: 'Extra salsa', category: 'extra' },
    { id: 8, label: 'Extra picante', category: 'extra' },
    { id: 9, label: 'Bien cocido', category: 'coccion' },
    { id: 10, label: 'Término medio', category: 'coccion' },
    { id: 11, label: 'Poco cocido', category: 'coccion' },
    { id: 12, label: 'Sin hielo', category: 'bebida' },
    { id: 13, label: 'Con limón', category: 'bebida' },
    { id: 14, label: 'Para llevar', category: 'otro' },
    { id: 15, label: 'Separado', category: 'otro' },
  ];
  
  // Custom/Special Product Modal
  const [showCustomProductModal, setShowCustomProductModal] = useState(false);
  const [customProductName, setCustomProductName] = useState('');
  const [customProductPrice, setCustomProductPrice] = useState('');
  const [customProductNotes, setCustomProductNotes] = useState('');
  const [customProductIngredients, setCustomProductIngredients] = useState([]);
  const [availableIngredients, setAvailableIngredients] = useState({ supplies: [], processed: [], all: [] });
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [ingredientQuantity, setIngredientQuantity] = useState('');
  const [ingredientUnit, setIngredientUnit] = useState('gr');
  const [saveToMenu, setSaveToMenu] = useState(false);
  const [creatingCustomProduct, setCreatingCustomProduct] = useState(false);
  
  // Customer & Order Info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerStreet, setCustomerStreet] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [customerFloor, setCustomerFloor] = useState('');
  const [customerNeighborhood, setCustomerNeighborhood] = useState('');
  const [customerColonia, setCustomerColonia] = useState('');
  const [customerBirthday, setCustomerBirthday] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Full customer object with history
  const [showCustomerHistory, setShowCustomerHistory] = useState(false);
  const [numGuests, setNumGuests] = useState('');
  const [comments, setComments] = useState('');
  const [deliveryDriver, setDeliveryDriver] = useState('');
  const [estimatedTime, setEstimatedTime] = useState('');
  const [deliveryCost, setDeliveryCost] = useState('');
  
  // Discount & Cancellation
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState('fixed'); // 'fixed' or 'percent'
  const [discountReason, setDiscountReason] = useState('');
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [discountOrderId, setDiscountOrderId] = useState(null);
  const [discountOrderSubtotal, setDiscountOrderSubtotal] = useState(0);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelItemId, setCancelItemId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  
  // Split Bill
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [splitOrderId, setSplitOrderId] = useState(null);
  const [splitItems, setSplitItems] = useState({}); // {product_id: quantity} for single split
  const [splitCustomerName, setSplitCustomerName] = useState('');
  const [splitMode, setSplitMode] = useState('single'); // 'single' or 'multiple'
  const [numSplitBills, setNumSplitBills] = useState(2); // Number of bills to create
  const [multiSplitAssignments, setMultiSplitAssignments] = useState({}); // {productId_index: billNumber}
  
  // Selected order for editing (adding products to existing order)
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAddToOrderPanel, setShowAddToOrderPanel] = useState(false);

  // Cancel existing order item
  const [cancelExistingItemModalOpen, setCancelExistingItemModalOpen] = useState(false);
  const [cancelExistingOrderId, setCancelExistingOrderId] = useState(null);
  const [cancelExistingItemIndex, setCancelExistingItemIndex] = useState(null);
  const [cancelExistingItemName, setCancelExistingItemName] = useState('');
  const [cancelExistingReason, setCancelExistingReason] = useState('');

  // Cancel/Void entire order
  const [voidOrderModalOpen, setVoidOrderModalOpen] = useState(false);
  const [voidOrderId, setVoidOrderId] = useState(null);
  const [voidOrderNumber, setVoidOrderNumber] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  // Change Table Modal
  const [changeTableModalOpen, setChangeTableModalOpen] = useState(false);
  const [changeTableOrderId, setChangeTableOrderId] = useState(null);
  const [changeTableCurrentNumber, setChangeTableCurrentNumber] = useState(null);
  const [changeTableNewId, setChangeTableNewId] = useState(null);

  // Close Table Modal (FUDO style)
  const [closeTableModalOpen, setCloseTableModalOpen] = useState(false);
  const [closeTableData, setCloseTableData] = useState(null);
  const [closeTipAmount, setCloseTipAmount] = useState('');
  const [closeTipMethod, setCloseTipMethod] = useState('efectivo');
  const [closePaymentMethod, setClosePaymentMethod] = useState('efectivo');
  const [closePaymentAmount, setClosePaymentAmount] = useState('');
  const [closePartialPayment, setClosePartialPayment] = useState(false);

  // Weight input modal for products sold by weight
  const [weightModalOpen, setWeightModalOpen] = useState(false);
  const [weightProduct, setWeightProduct] = useState(null);
  const [weightInput, setWeightInput] = useState('');

  // Selectable ingredients modal
  const [ingredientModalOpen, setIngredientModalOpen] = useState(false);
  const [ingredientProduct, setIngredientProduct] = useState(null);
  const [selectedIngredients, setSelectedIngredients] = useState({});

  // Customer search
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [isReturningCustomer, setIsReturningCustomer] = useState(false);

  // VIP Customer Alert Modal
  const [vipAlertModalOpen, setVipAlertModalOpen] = useState(false);
  const [vipAlertCustomer, setVipAlertCustomer] = useState(null);
  const [vipRedeemableProducts, setVipRedeemableProducts] = useState([]);

  // Edit customer info modal
  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
  const [editCustomerOrder, setEditCustomerOrder] = useState(null);
  const [editCustomerName, setEditCustomerName] = useState('');
  const [editCustomerPhone, setEditCustomerPhone] = useState('');
  const [editCustomerReason, setEditCustomerReason] = useState('');

  // Duplicate customer detection modal
  const [duplicateCustomerModalOpen, setDuplicateCustomerModalOpen] = useState(false);
  const [duplicateCustomerData, setDuplicateCustomerData] = useState(null);
  const [pendingOrderCreation, setPendingOrderCreation] = useState(null); // Stores the order creation function to resume

  // Selected order for Mostrador/Domicilio panel (like Mesa panel)
  const [selectedTakeoutOrder, setSelectedTakeoutOrder] = useState(null);
  const [selectedDeliveryOrder, setSelectedDeliveryOrder] = useState(null);

  // Loyalty Points Redemption Modal
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemPin, setRedeemPin] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [pendingRedemptions, setPendingRedemptions] = useState([]);
  const [showLoyaltyPanel, setShowLoyaltyPanel] = useState(false);

  // Table areas
  const tableAreas = [...new Set(tables.map(t => t.area || 'Principal'))];
  const [selectedArea, setSelectedArea] = useState('');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [branchId, isMatriz]); // Recargar cuando cambie la sucursal o el rol

  useEffect(() => {
    if (tableAreas.length > 0 && !selectedArea) {
      setSelectedArea(tableAreas[0]);
    }
  }, [tableAreas]);

  const fetchData = async () => {
    try {
      // OPTIMIZED: Single API call for all POS data
      const branchParams = branchId ? `?branch_id=${branchId}&is_matriz=${isMatriz}` : '';
      
      const res = await api.get(`/pos/init-data${branchParams}`);
      const data = res.data;
      
      setProducts(data.products);
      setCategories(data.categories);
      setFlatCategories(data.flat_categories);
      setTables(data.tables);
      setAllOrders(data.orders);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const branchQuery = branchId ? `?branch_id=${branchId}` : '';
      const ordersRes = await api.get(`/orders${branchQuery}`);
      const orders = ordersRes.data;
      
      // Auto-fix orders with total=0 but have items
      const ordersToFix = orders.filter(o => 
        (o.total === 0 || o.total === null) && 
        o.items?.length > 0 && 
        o.status !== 'completed' && 
        o.status !== 'cancelled'
      );
      
      if (ordersToFix.length > 0) {
        console.log(`Recalculando ${ordersToFix.length} órdenes con total incorrecto...`);
        try {
          await api.post('/orders/recalculate-all');
          // Refetch orders after recalculation
          const updatedRes = await api.get(`/orders${branchQuery}`);
          setAllOrders(updatedRes.data);
          return;
        } catch (e) {
          console.error('Error recalculando órdenes:', e);
        }
      }
      
      setAllOrders(orders);
    } catch (error) {
      console.error('Error fetching orders');
    }
  };

  // Fetch pending loyalty redemptions
  const fetchPendingRedemptions = async () => {
    try {
      const res = await api.get('/customer-portal/admin/pending-redemptions');
      setPendingRedemptions(res.data.redemptions || []);
    } catch (error) {
      console.error('Error fetching redemptions:', error);
    }
  };

  // Verify and complete a loyalty redemption
  const verifyRedemption = async () => {
    if (!redeemCode.trim()) {
      toast.error('Ingresa el código de canje');
      return;
    }
    if (!redeemPin.trim()) {
      toast.error('Ingresa el PIN');
      return;
    }

    setRedeemLoading(true);
    try {
      // First, find the redemption by code
      const searchRes = await api.get(`/customer-portal/admin/pending-redemptions`);
      const redemptions = searchRes.data.redemptions || [];
      const redemption = redemptions.find(r => 
        r.redemption_code.toUpperCase() === redeemCode.toUpperCase().trim()
      );

      if (!redemption) {
        toast.error('Código de canje no encontrado o ya procesado');
        setRedeemLoading(false);
        return;
      }

      // Verify with PIN
      const verifyRes = await api.post('/customer-portal/admin/verify-redemption', {
        redemption_id: redemption.redemption_id,
        pin: redeemPin.trim()
      });

      if (verifyRes.data.success) {
        toast.success(`✅ Canje completado: ${verifyRes.data.product_name}`);
        toast.info(`Cliente: ${verifyRes.data.customer_name} | -${verifyRes.data.points_deducted} puntos`);
        
        // Reset modal
        setRedeemModalOpen(false);
        setRedeemCode('');
        setRedeemPin('');
        
        // Refresh pending redemptions
        fetchPendingRedemptions();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al verificar canje';
      toast.error(msg);
    } finally {
      setRedeemLoading(false);
    }
  };

  // Effect to ensure panel stays open after order creation
  useEffect(() => {
    if (keepPanelOpenRef.current && selectedTable && !showNewOrderPanel) {
      setShowNewOrderPanel(true);
      // Reset ref after a short delay to allow for the state to settle
      setTimeout(() => {
        keepPanelOpenRef.current = false;
      }, 500);
    }
  }, [allOrders, selectedTable, showNewOrderPanel]);

  // Search customers by phone
  const searchCustomerByPhone = async (phone) => {
    if (!phone || phone.length < 3) {
      setCustomerSuggestions([]);
      setShowCustomerSuggestions(false);
      return;
    }
    
    try {
      const res = await api.get(`/customers/search?q=${encodeURIComponent(phone)}`);
      setCustomerSuggestions(res.data);
      setShowCustomerSuggestions(res.data.length > 0);
    } catch (error) {
      console.error('Error searching customer');
    }
  };

  // Search customer by name
  const searchCustomerByName = async (name) => {
    if (!name || name.length < 2) {
      setCustomerSuggestions([]);
      setShowCustomerSuggestions(false);
      return;
    }
    
    try {
      const res = await api.get(`/customers/search?q=${encodeURIComponent(name)}`);
      setCustomerSuggestions(res.data);
      setShowCustomerSuggestions(res.data.length > 0);
    } catch (error) {
      console.error('Error searching customer by name');
    }
  };

  // Handle phone change with debounce
  const handlePhoneChange = (e) => {
    const phone = e.target.value;
    setCustomerPhone(phone);
    setIsReturningCustomer(false);
    setSelectedCustomer(null); // Clear selected customer when user changes phone manually
    
    // Debounced search
    clearTimeout(window.customerSearchTimeout);
    window.customerSearchTimeout = setTimeout(() => {
      searchCustomerByPhone(phone);
    }, 300);
  };

  // Handle name change with debounce
  const handleNameChange = (e) => {
    const name = e.target.value;
    setCustomerName(name);
    setIsReturningCustomer(false);
    setSelectedCustomer(null); // Clear selected customer when user changes name manually
    
    // Debounced search by name
    clearTimeout(window.customerNameSearchTimeout);
    window.customerNameSearchTimeout = setTimeout(() => {
      searchCustomerByName(name);
    }, 300);
  };

  // Select customer from suggestions
  const selectCustomer = async (customer) => {
    setCustomerPhone(customer.phone || '');
    setCustomerName(customer.name || '');
    setCustomerAddress(customer.address || '');
    setCustomerColonia(customer.colonia || '');
    setCustomerBirthday(customer.birthday || '');
    setSelectedCustomer(customer); // Store full customer object with stats
    setIsReturningCustomer(true);
    setShowCustomerSuggestions(false);
    setCustomerSuggestions([]);
    setShowCustomerHistory(true); // Show history panel
    
    // Show VIP loyalty alert if customer has points
    if (customer.loyalty?.points_available > 0) {
      const points = customer.loyalty.points_available;
      const canRedeem = customer.loyalty.can_redeem;
      
      if (canRedeem) {
        // Play VIP notification sound
        playVipNotificationSound();
        
        // Fetch redeemable products for this customer
        try {
          const res = await api.get(`/customer-portal/redeemable-products/${user.user_id}?phone=${customer.phone}`);
          const products = res.data.products || [];
          const affordableProducts = products.filter(p => p.can_afford);
          
          // Show VIP Alert Modal
          setVipAlertCustomer({
            ...customer,
            points: points,
            pesos: customer.loyalty.points_value_pesos,
            affordableProducts: affordableProducts
          });
          setVipRedeemableProducts(affordableProducts);
          setVipAlertModalOpen(true);
        } catch (error) {
          console.error('Error fetching redeemable products:', error);
          // Fallback to toast if API fails
          toast.success(
            `🎉 ¡${customer.name} tiene ${points} puntos! Puede canjear productos.`,
            { duration: 5000 }
          );
        }
      } else {
        toast.info(
          `⭐ ${customer.name} tiene ${points} puntos ($${customer.loyalty.points_value_pesos}). ¡Sigue acumulando!`,
          { duration: 4000 }
        );
      }
    } else {
      toast.success(`¡Bienvenido de nuevo, ${customer.name}!`);
    }
  };

  // Play VIP notification sound (3 ascending tones)
  const playVipNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const playTone = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(frequency, startTime);
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.4, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = audioContext.currentTime;
      // 3 ascending celebratory tones
      playTone(523.25, now, 0.15);        // C5
      playTone(659.25, now + 0.15, 0.15); // E5
      playTone(783.99, now + 0.30, 0.3);  // G5 (longer)
      
    } catch (e) {
      console.error('Audio error:', e);
    }
  };

  // Apply loyalty redemption to cart
  const applyLoyaltyRedemption = async (product) => {
    if (!selectedCustomer) {
      toast.error('No hay cliente seleccionado');
      return;
    }

    try {
      // Request redemption from backend
      const res = await fetch(`${API}/api/customer-portal/request-redemption`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: selectedCustomer.phone,
          product_id: product.product_id,
          restaurant_id: user.user_id
        })
      });

      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Error al solicitar canje');
        return;
      }

      // Add product to cart with price 0 and mark as redemption
      const redemptionItem = {
        product_id: product.product_id,
        product_name: `🎁 CANJE: ${product.name}`,
        price: 0,
        quantity: 1,
        subtotal: 0,
        is_redemption: true,
        redemption_code: data.code,
        redemption_pin: data.pin,
        points_cost: product.points_cost
      };

      setCart(prev => [...prev, redemptionItem]);
      setShowLoyaltyPanel(false);
      
      toast.success(`✅ Canje aplicado: ${product.name} (${product.points_cost} pts)`);
      toast.info(`Código: ${data.code} | PIN: ${data.pin}`, { duration: 8000 });

      // Refresh customer data to update points
      searchCustomerByPhone(selectedCustomer.phone);
    } catch (error) {
      toast.error('Error al procesar canje');
      console.error(error);
    }
  };
  
  // Clear selected customer
  const clearSelectedCustomer = () => {
    setCustomerPhone('');
    setCustomerName('');
    setCustomerAddress('');
    setCustomerColonia('');
    setCustomerBirthday('');
    setSelectedCustomer(null);
    setIsReturningCustomer(false);
    setShowCustomerHistory(false);
  };

  const getCategoryIdsWithChildren = (categoryId) => {
    const ids = [categoryId];
    flatCategories.forEach(c => {
      if (c.parent_id === categoryId) ids.push(c.category_id);
    });
    return ids;
  };

  const filteredProducts = products.filter(p => {
    let matchesCategory = selectedCategory === 'all';
    if (!matchesCategory) {
      const categoryIds = getCategoryIdsWithChildren(selectedCategory);
      matchesCategory = categoryIds.includes(p.category_id);
    }
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch && p.is_available;
  });

  // ==================== CUSTOM PRODUCT FUNCTIONS ====================
  
  const openCustomProductModal = async () => {
    setShowCustomProductModal(true);
    setCustomProductName('');
    setCustomProductPrice('');
    setCustomProductNotes('');
    setCustomProductIngredients([]);
    setIngredientSearch('');
    setSelectedIngredient(null);
    setIngredientQuantity('');
    setIngredientUnit('gr');
    setSaveToMenu(false);
    
    // Fetch available ingredients
    try {
      const res = await fetch(`${API}/api/special-products/ingredients-list`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableIngredients(data);
      }
    } catch (err) {
      console.error('Error fetching ingredients:', err);
    }
  };
  
  const addIngredientToCustomProduct = () => {
    if (!selectedIngredient || !ingredientQuantity) {
      toast.error('Selecciona un ingrediente y cantidad');
      return;
    }
    
    const exists = customProductIngredients.find(i => i.id === selectedIngredient.id);
    if (exists) {
      toast.error('Este ingrediente ya está agregado');
      return;
    }
    
    const newIngredient = {
      type: selectedIngredient.type,
      id: selectedIngredient.id,
      name: selectedIngredient.name,
      quantity: parseFloat(ingredientQuantity),
      unit: ingredientUnit,
      cost_per_unit: selectedIngredient.cost_per_unit
    };
    
    setCustomProductIngredients([...customProductIngredients, newIngredient]);
    setSelectedIngredient(null);
    setIngredientQuantity('');
    setIngredientSearch('');
  };
  
  const removeIngredientFromCustomProduct = (id) => {
    setCustomProductIngredients(customProductIngredients.filter(i => i.id !== id));
  };
  
  const calculateCustomProductCost = () => {
    return customProductIngredients.reduce((total, ing) => {
      let qty = ing.quantity;
      if (ing.unit === 'gr') qty = qty / 1000;
      return total + (qty * ing.cost_per_unit);
    }, 0);
  };
  
  const createAndAddCustomProduct = async () => {
    if (!customProductName || !customProductPrice) {
      toast.error('Ingresa nombre y precio');
      return;
    }
    // Ingredients are now optional - user can create product without them
    
    setCreatingCustomProduct(true);
    try {
      // Create special product
      const res = await fetch(`${API}/api/special-products/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: customProductName,
          price: parseFloat(customProductPrice),
          ingredients: customProductIngredients,
          notes: customProductNotes,
          save_to_catalog: saveToMenu
        })
      });
      
      if (!res.ok) throw new Error('Error al crear producto');
      
      const specialProduct = await res.json();
      
      // Add to cart
      const cartItem = {
        product_id: `special_${specialProduct.special_product_id}`,
        product_name: specialProduct.name,
        name: specialProduct.name,  // For cart display consistency
        price: specialProduct.price,  // Used by cart calculations
        unit_price: specialProduct.price,
        quantity: 1,
        subtotal: specialProduct.price,
        is_special_product: true,
        cost: specialProduct.cost
      };
      
      setCart(prevCart => [...prevCart, cartItem]);
      setShowCustomProductModal(false);
      toast.success(`${specialProduct.name} agregado. Ganancia: $${specialProduct.profit.toFixed(2)}`);
      
    } catch (err) {
      toast.error('Error al crear producto personalizado');
      console.error(err);
    } finally {
      setCreatingCustomProduct(false);
    }
  };
  
  const filteredIngredients = availableIngredients.all?.filter(i => 
    i.name.toLowerCase().includes(ingredientSearch.toLowerCase())
  ) || [];

  const addToCart = (product) => {
    // Clear search query immediately when selecting any product
    setSearchQuery('');
    
    // If product is sold by weight, open weight input modal
    if (product.sold_by_weight) {
      setWeightProduct(product);
      setWeightInput('');
      setWeightModalOpen(true);
      return;
    }
    
    // If product has selectable ingredients, open ingredient selection modal
    if (product.has_selectable_ingredients && product.selectable_ingredients?.length > 0) {
      setIngredientProduct(product);
      // Initialize selection with defaults
      const initialSelection = {};
      product.selectable_ingredients.forEach(ing => {
        initialSelection[ing.processed_id] = ing.is_default;
      });
      setSelectedIngredients(initialSelection);
      setIngredientModalOpen(true);
      return;
    }
    
    // Open notes modal for the product
    setNotesProduct(product);
    setProductNotes('');
    setSelectedQuickNotes([]);
    setShowNotesModal(true);
  };
  
  // Add product to cart with notes (called after notes modal)
  const addToCartWithNotes = () => {
    if (!notesProduct) return;
    
    // Combine quick notes and custom note
    const allNotes = [...selectedQuickNotes];
    if (productNotes.trim()) {
      allNotes.push(productNotes.trim());
    }
    const finalNotes = allNotes.join(', ');
    
    addProductToCartWithQuantity(notesProduct, 1, null, finalNotes);
    
    // Show toast
    if (finalNotes) {
      toast.success(`${notesProduct.name} (${finalNotes})`);
    } else {
      toast.success(`${notesProduct.name} agregado`);
    }
    
    // Close modal
    setShowNotesModal(false);
    setNotesProduct(null);
    setProductNotes('');
    setSelectedQuickNotes([]);
  };
  
  // Add product without notes (quick add)
  const addToCartQuick = () => {
    if (!notesProduct) return;
    addProductToCartWithQuantity(notesProduct, 1, null, '');
    toast.success(`${notesProduct.name} agregado`);
    setShowNotesModal(false);
    setNotesProduct(null);
    setProductNotes('');
    setSelectedQuickNotes([]);
  };
  
  // Toggle quick note selection
  const toggleQuickNote = (noteLabel) => {
    setSelectedQuickNotes(prev => 
      prev.includes(noteLabel) 
        ? prev.filter(n => n !== noteLabel)
        : [...prev, noteLabel]
    );
  };
  
  // Add product with specific quantity (used for weight-based products)
  const addProductToCartWithQuantity = (product, quantity, customIngredients = null, itemNotes = '') => {
    const finalPrice = product.sold_by_weight ? product.price * quantity : product.price;
    
    // Clear search query after adding product
    setSearchQuery('');
    
    setCart(prev => {
      // For weight-based products, products with custom ingredients, or products with notes, always add as new item
      if (product.sold_by_weight || customIngredients || itemNotes) {
        return [...prev, { 
          ...product, 
          quantity: quantity, 
          cancelled: false, 
          cancelReason: null,
          weight_quantity: product.sold_by_weight ? quantity : null,
          calculated_price: finalPrice,
          selected_ingredients: customIngredients, // Store selected ingredients
          item_notes: itemNotes || '' // Store item notes
        }];
      }
      
      // For regular products without notes, check if exists and increment
      const existing = prev.find(item => item.product_id === product.product_id && !item.cancelled && !item.selected_ingredients && !item.item_notes);
      if (existing) {
        return prev.map(item => 
          item.product_id === product.product_id && !item.cancelled && !item.selected_ingredients && !item.item_notes
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1, cancelled: false, cancelReason: null, item_notes: '' }];
    });
  };

  // Confirm ingredient selection and add to cart
  const confirmIngredientSelection = () => {
    if (!ingredientProduct) return;
    
    // Build selected ingredients array
    const selectedList = ingredientProduct.selectable_ingredients
      .map(ing => ({
        processed_id: ing.processed_id,
        name: ing.name,
        selected: selectedIngredients[ing.processed_id] || false
      }));
    
    // Check at least one ingredient is selected
    const hasSelection = selectedList.some(ing => ing.selected);
    if (!hasSelection) {
      toast.error('Selecciona al menos un ingrediente');
      return;
    }
    
    // Add to cart with custom ingredients
    addProductToCartWithQuantity(ingredientProduct, 1, selectedList, '');
    
    // Build description for toast
    const selectedNames = selectedList.filter(i => i.selected).map(i => i.name).join(', ');
    toast.success(`${ingredientProduct.name} con: ${selectedNames}`);
    
    // Close modal
    setIngredientModalOpen(false);
    setIngredientProduct(null);
    setSelectedIngredients({});
  };
  
  // Confirm weight and add to cart
  const confirmWeightProduct = () => {
    const weight = parseFloat(weightInput);
    if (isNaN(weight) || weight <= 0) {
      toast.error('Ingresa una cantidad válida');
      return;
    }
    addProductToCartWithQuantity(weightProduct, weight);
    setWeightModalOpen(false);
    setWeightProduct(null);
    setWeightInput('');
    toast.success(`${weightProduct.name} - ${weight} ${weightProduct.weight_unit || 'kg'} agregado`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id === productId && !item.cancelled) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0 || item.cancelled));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  // Cancel item with reason
  const openCancelModal = (productId) => {
    setCancelItemId(productId);
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const confirmCancelItem = () => {
    if (!cancelReason.trim()) {
      toast.error('Por favor ingresa un motivo de cancelación');
      return;
    }
    setCart(prev => prev.map(item => 
      item.product_id === cancelItemId 
        ? { ...item, cancelled: true, cancelReason: cancelReason.trim() }
        : item
    ));
    setCancelModalOpen(false);
    setCancelItemId(null);
    setCancelReason('');
    toast.success('Item cancelado');
  };

  // Restore cancelled item
  const restoreCancelledItem = (productId) => {
    setCart(prev => prev.map(item => 
      item.product_id === productId 
        ? { ...item, cancelled: false, cancelReason: null }
        : item
    ));
    toast.success('Item restaurado');
  };

  // Open discount modal for an order
  const openDiscountModal = (order) => {
    setDiscountOrderId(order.order_id);
    setDiscountOrderSubtotal(order.subtotal || order.total || 0);
    setDiscountAmount(order.discount_amount || 0);
    setDiscountReason(order.discount_reason || '');
    setDiscountType('fixed');
    setDiscountModalOpen(true);
  };

  // Apply discount to existing order
  const applyDiscountToOrder = async () => {
    if (!discountOrderId) return;
    
    // Use the discount amount directly (no conversion needed)
    const discountValue = discountAmount;
    
    if (discountValue > discountOrderSubtotal) {
      toast.error('El descuento no puede ser mayor al subtotal');
      return;
    }
    
    if (!discountReason.trim()) {
      toast.error('Debe ingresar un motivo para el descuento');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/orders/${discountOrderId}/edit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          discount_amount: discountValue,
          discount_reason: discountReason.trim(),
          reason: `Descuento $${discountValue}: ${discountReason.trim()}`
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Error al aplicar descuento');
      }
      
      toast.success(`Descuento de $${discountValue.toFixed(2)} aplicado`);
      setDiscountModalOpen(false);
      setDiscountOrderId(null);
      setDiscountAmount(0);
      setDiscountReason('');
      fetchOrders();
    } catch (err) {
      toast.error(err.message || 'Error al aplicar descuento');
    } finally {
      setProcessing(false);
    }
  };

  // Void/Cancel entire order functions
  const openVoidOrderModal = (orderId, orderNumber) => {
    setVoidOrderId(orderId);
    setVoidOrderNumber(orderNumber);
    setVoidReason('');
    setVoidOrderModalOpen(true);
  };

  const confirmVoidOrder = async () => {
    if (!voidReason.trim()) {
      toast.error('Por favor ingresa un motivo de cancelación');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/orders/${voidOrderId}/void`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ reason: voidReason.trim() })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Error al cancelar');
      }
      
      toast.success(`Orden #${voidOrderNumber} cancelada`);
      setVoidOrderModalOpen(false);
      setVoidOrderId(null);
      setVoidOrderNumber(null);
      setVoidReason('');
      fetchOrders();
    } catch (err) {
      toast.error(err.message || 'Error al cancelar la orden');
    } finally {
      setProcessing(false);
    }
  };

  // Change Table functions
  const openChangeTableModal = (orderId, currentTableNumber) => {
    setChangeTableOrderId(orderId);
    setChangeTableCurrentNumber(currentTableNumber);
    setChangeTableNewId(null);
    setChangeTableModalOpen(true);
  };

  const confirmChangeTable = async () => {
    if (!changeTableNewId) {
      toast.error('Por favor selecciona una mesa destino');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/orders/${changeTableOrderId}/change-table`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ new_table_id: changeTableNewId })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Error al cambiar mesa');
      }
      
      toast.success(data.message);
      setChangeTableModalOpen(false);
      setChangeTableOrderId(null);
      setChangeTableCurrentNumber(null);
      setChangeTableNewId(null);
      setSelectedTable(null);
      fetchOrders();
      fetchData(); // Refresh tables and orders
    } catch (err) {
      toast.error(err.message || 'Error al cambiar mesa');
    } finally {
      setProcessing(false);
    }
  };

  // Cancel existing order item functions
  const openCancelExistingItemModal = (orderId, itemIndex, itemName) => {
    setCancelExistingOrderId(orderId);
    setCancelExistingItemIndex(itemIndex);
    setCancelExistingItemName(itemName);
    setCancelExistingReason('');
    setCancelExistingItemModalOpen(true);
  };

  const confirmCancelExistingItem = async () => {
    if (!cancelExistingReason.trim()) {
      toast.error('Por favor ingresa un motivo de cancelación');
      return;
    }
    
    setProcessing(true);
    try {
      await api.put(`/orders/${cancelExistingOrderId}/items/${cancelExistingItemIndex}/cancel`, {
        reason: cancelExistingReason.trim()
      });
      
      toast.success(`"${cancelExistingItemName}" cancelado`);
      setCancelExistingItemModalOpen(false);
      setCancelExistingOrderId(null);
      setCancelExistingItemIndex(null);
      setCancelExistingItemName('');
      setCancelExistingReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar el item');
    } finally {
      setProcessing(false);
    }
  };

  // Edit Customer Info functions
  const openEditCustomerModal = (order) => {
    setEditCustomerOrder(order);
    setEditCustomerName(order.customer_name || '');
    setEditCustomerPhone(order.customer_phone || '');
    setEditCustomerReason('');
    setEditCustomerModalOpen(true);
  };

  const confirmEditCustomer = async () => {
    if (!editCustomerReason.trim()) {
      toast.error('Por favor ingresa un motivo para la edición');
      return;
    }
    
    setProcessing(true);
    try {
      await api.put(`/orders/${editCustomerOrder.order_id}/edit`, {
        customer_name: editCustomerName.trim() || null,
        customer_phone: editCustomerPhone.trim() || null,
        reason: editCustomerReason.trim()
      });
      
      toast.success('Información del cliente actualizada');
      setEditCustomerModalOpen(false);
      setEditCustomerOrder(null);
      setEditCustomerName('');
      setEditCustomerPhone('');
      setEditCustomerReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la información');
    } finally {
      setProcessing(false);
    }
  };

  // Handle duplicate customer - use existing customer
  const handleUseExistingCustomer = () => {
    if (duplicateCustomerData) {
      // Select the existing customer
      selectCustomer(duplicateCustomerData);
      toast.success(`Cliente existente seleccionado: ${duplicateCustomerData.name}`);
    }
    setDuplicateCustomerModalOpen(false);
    setDuplicateCustomerData(null);
    
    // Resume the pending order creation
    if (pendingOrderCreation === 'handleCreateOrder') {
      handleCreateOrder(true); // Skip duplicate check this time
    } else if (pendingOrderCreation === 'handleCreateAndMarkReady') {
      handleCreateAndMarkReady(true);
    }
    setPendingOrderCreation(null);
  };

  // Handle duplicate customer - create new anyway
  const handleCreateNewCustomerAnyway = async () => {
    setDuplicateCustomerModalOpen(false);
    setDuplicateCustomerData(null);
    
    // Force create the new customer and resume order
    if (pendingOrderCreation === 'handleCreateOrder') {
      // Set selectedCustomer to null to force new registration
      setSelectedCustomer(null);
      handleCreateOrder(true); // Skip duplicate check
    } else if (pendingOrderCreation === 'handleCreateAndMarkReady') {
      setSelectedCustomer(null);
      handleCreateAndMarkReady(true);
    }
    setPendingOrderCreation(null);
  };

  // Cancel duplicate handling
  const handleCancelDuplicateModal = () => {
    setDuplicateCustomerModalOpen(false);
    setDuplicateCustomerData(null);
    setPendingOrderCreation(null);
    toast.info('Creación de pedido cancelada');
  };

  // Split Bill functions
  const openSplitModal = (order) => {
    setSplitOrderId(order.order_id);
    setSplitItems({});
    setSplitCustomerName('');
    setSplitMode('single');
    setNumSplitBills(2);
    setMultiSplitAssignments({});
    setSplitModalOpen(true);
  };

  const updateSplitQuantity = (productId, maxQty, delta) => {
    setSplitItems(prev => {
      const current = prev[productId] || 0;
      const newQty = Math.max(0, Math.min(maxQty, current + delta));
      if (newQty === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: newQty };
    });
  };

  // Assign a product unit to a specific bill number in multi-split mode
  const assignToBill = (productId, unitIndex, billNumber) => {
    const key = `${productId}_${unitIndex}`;
    setMultiSplitAssignments(prev => ({
      ...prev,
      [key]: billNumber
    }));
  };

  // Get items assigned to a specific bill number
  const getItemsForBill = (order, billNumber) => {
    const items = [];
    order.items?.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        const key = `${item.product_id}_${i}`;
        if (multiSplitAssignments[key] === billNumber) {
          const existing = items.find(x => x.product_id === item.product_id);
          if (existing) {
            existing.quantity += 1;
          } else {
            items.push({
              product_id: item.product_id,
              product_name: item.product_name,
              unit_price: item.unit_price,
              quantity: 1
            });
          }
        }
      }
    });
    return items;
  };

  // Calculate total for a specific bill
  const getBillTotal = (order, billNumber) => {
    const items = getItemsForBill(order, billNumber);
    return items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  };

  const getSplitTotal = (order) => {
    if (!order) return 0;
    let total = 0;
    for (const item of order.items) {
      const qty = splitItems[item.product_id] || 0;
      total += (item.unit_price || 0) * qty;
    }
    return total; // Precios ya incluyen IVA
  };

  const handleSplitBill = async () => {
    const itemsToSplit = Object.entries(splitItems)
      .filter(([_, qty]) => qty > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity }));

    if (itemsToSplit.length === 0) {
      toast.error('Selecciona al menos un producto para dividir');
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post(`/orders/${splitOrderId}/split`, {
        order_id: splitOrderId,
        items_to_split: itemsToSplit,
        new_customer_name: splitCustomerName || null
      });
      
      const newOrder = response.data.new_order;
      const newOrderTotal = response.data.new_order_total;
      const originalTotal = response.data.original_order_total;
      
      // Mostrar alerta persistente con información clara
      toast.success(
        `✅ Cuenta dividida!\n\n` +
        `📋 Orden original: $${originalTotal?.toFixed(2) || '0.00'}\n` +
        `📋 Nueva orden #${newOrder.order_number}: $${newOrderTotal?.toFixed(2) || '0.00'}\n\n` +
        `⚠️ RECUERDA: Debes cobrar AMBAS cuentas por separado`,
        { duration: 10000 }
      );
      
      setSplitModalOpen(false);
      setSplitOrderId(null);
      setSplitItems({});
      setSplitCustomerName('');
      
      // Refrescar datos y seleccionar automáticamente la nueva orden
      await fetchData();
      
      // Después de refrescar, seleccionar la nueva orden para que sea visible
      setTimeout(() => {
        setSelectedOrder(newOrder.order_id);
        toast.info(`👆 Nueva cuenta #${newOrder.order_number} seleccionada - Lista para cobrar`, { duration: 5000 });
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al dividir la cuenta');
    } finally {
      setProcessing(false);
    }
  };

  // Handle multiple split - creates multiple new orders
  const handleMultiSplitBill = async () => {
    const order = allOrders.find(o => o.order_id === splitOrderId);
    if (!order) return;

    // Check that all items are assigned
    let totalAssigned = 0;
    let totalItems = order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
    
    Object.values(multiSplitAssignments).forEach(bill => {
      if (bill > 0) totalAssigned++;
    });

    if (totalAssigned === 0) {
      toast.error('Asigna al menos un producto a una cuenta');
      return;
    }

    setProcessing(true);
    try {
      // Create new orders for bills 2 to numSplitBills (bill 1 stays as original)
      const createdOrders = [];
      
      for (let billNum = 2; billNum <= numSplitBills; billNum++) {
        const itemsForBill = getItemsForBill(order, billNum);
        if (itemsForBill.length > 0) {
          const response = await api.post(`/orders/${splitOrderId}/split`, {
            order_id: splitOrderId,
            items_to_split: itemsForBill.map(item => ({
              product_id: item.product_id,
              quantity: item.quantity
            })),
            new_customer_name: `Cuenta ${billNum}`
          });
          createdOrders.push({
            number: response.data.new_order.order_number,
            total: response.data.new_order_total,
            id: response.data.new_order.order_id
          });
        }
      }
      
      if (createdOrders.length > 0) {
        const ordersList = createdOrders.map(o => `#${o.number} ($${o.total?.toFixed(2)})`).join(', ');
        toast.success(
          `✅ ¡Cuenta dividida en ${createdOrders.length + 1} cuentas!\n\n` +
          `📋 Nuevas órdenes: ${ordersList}\n\n` +
          `⚠️ RECUERDA: Debes cobrar TODAS las cuentas por separado`,
          { duration: 10000 }
        );
      }
      
      setSplitModalOpen(false);
      setSplitOrderId(null);
      setSplitItems({});
      setMultiSplitAssignments({});
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al dividir la cuenta');
    } finally {
      setProcessing(false);
    }
  };

  const clearForm = (keepTableAndPanel = false) => {
    setCart([]);
    setSearchQuery('');
    if (!keepTableAndPanel) {
      setSelectedTable(null);
      setShowNewOrderPanel(false);
    }
    setCustomerName('');
    setCustomerPhone('');
    setCustomerAddress('');
    setCustomerStreet('');
    setCustomerNumber('');
    setCustomerFloor('');
    setCustomerNeighborhood('');
    setCustomerColonia('');
    setCustomerBirthday('');
    setSelectedCustomer(null);
    setShowCustomerHistory(false);
    setIsReturningCustomer(false);
    setNumGuests('');
    setComments('');
    setDeliveryDriver('');
    setEstimatedTime('');
    setDeliveryCost('');
    setDiscountAmount(0);
    setDiscountType('fixed');
    setShowDiscountInput(false);
    setCancelModalOpen(false);
    setCancelItemId(null);
    setCancelReason('');
    setSplitModalOpen(false);
    setSplitOrderId(null);
    setSplitItems({});
    setSplitCustomerName('');
    setSelectedOrder(null);
    setShowAddToOrderPanel(false);
    setIsReturningCustomer(false);
    setCustomerSuggestions([]);
    setShowCustomerSuggestions(false);
  };

  // Select an order to add products
  const selectOrderForEditing = (order) => {
    setSelectedOrder(order);
    setShowAddToOrderPanel(true);
    setCart([]);
    setSearchQuery('');
  };

  // Add products to existing order
  const handleAddToExistingOrder = async () => {
    const activeItems = cart.filter(item => !item.cancelled);
    if (activeItems.length === 0) return toast.error('Agrega productos al pedido');
    if (!selectedOrder) return toast.error('No hay orden seleccionada');

    setProcessing(true);
    try {
      await api.put(`/orders/${selectedOrder.order_id}/add-items`, {
        items: activeItems.map(item => ({ 
          product_id: item.product_id, 
          quantity: item.quantity,
          notes: item.item_notes || null
        }))
      });
      
      toast.success('Productos agregados a la orden');
      setCart([]);
      setShowAddToOrderPanel(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar productos');
    } finally {
      setProcessing(false);
    }
  };

  // Calculate totals excluding cancelled items
  const activeCartItems = cart.filter(item => !item.cancelled);
  const subtotal = activeCartItems.reduce((sum, item) => {
    // For weight-based products, use calculated_price if available
    if (item.sold_by_weight && item.calculated_price) {
      return sum + item.calculated_price;
    }
    return sum + (item.price * item.quantity);
  }, 0);
  
  // Calculate discount
  const calculatedDiscount = discountType === 'percent' 
    ? (subtotal * discountAmount / 100) 
    : discountAmount;
  const subtotalAfterDiscount = Math.max(0, subtotal - calculatedDiscount);
  // Precios YA incluyen IVA - no agregar impuesto
  const tax = 0;
  const total = subtotalAfterDiscount;

  // Register or update customer with CRM fields
  const registerOrUpdateCustomer = async (forceCreate = false) => {
    // Only register if we have meaningful customer data
    if (!customerPhone && !customerName) return null;
    
    // If already a selected customer from search, optionally update their info
    if (selectedCustomer) {
      // Check if there are updates to make
      const hasUpdates = 
        (customerColonia && customerColonia !== selectedCustomer.colonia) ||
        (customerBirthday && customerBirthday !== selectedCustomer.birthday);
      
      if (hasUpdates) {
        try {
          const updateData = {};
          if (customerColonia) updateData.colonia = customerColonia;
          if (customerBirthday) updateData.birthday = customerBirthday;
          
          await api.put(`/customers/${selectedCustomer.customer_id}`, updateData);
        } catch (err) {
          console.error('Error updating customer:', err);
        }
      }
      return selectedCustomer.customer_id;
    }
    
    // Register new customer if we have phone and name
    if (customerPhone && customerName) {
      try {
        // Check for existing customer with same phone BEFORE creating (unless forced)
        if (!forceCreate) {
          const normalizedPhone = customerPhone.replace(/\D/g, '').slice(-10);
          const searchRes = await api.get(`/customers/search?q=${encodeURIComponent(normalizedPhone)}`);
          const existingCustomers = searchRes.data || [];
          
          // Find exact phone match
          const exactMatch = existingCustomers.find(c => {
            const cPhone = (c.phone || '').replace(/\D/g, '').slice(-10);
            return cPhone === normalizedPhone;
          });
          
          if (exactMatch) {
            // Customer already exists! Show duplicate modal
            setDuplicateCustomerData(exactMatch);
            setDuplicateCustomerModalOpen(true);
            return 'DUPLICATE_FOUND'; // Signal to caller
          }
        }
        
        const customerData = {
          phone: customerPhone,
          name: customerName,
          address: customerAddress || null,
          colonia: customerColonia || null,
          birthday: customerBirthday || null
        };
        
        const res = await api.post('/customers', customerData);
        return res.data.customer_id;
      } catch (err) {
        // Customer might already exist - this is fine
        console.log('Customer registration note:', err.response?.data?.detail || err.message);
      }
    }
    return null;
  };

  const handleCreateOrder = async (skipDuplicateCheck = false) => {
    const activeItems = cart.filter(item => !item.cancelled);
    if (activeItems.length === 0) return toast.error('Agrega productos al pedido');
    if (orderType === 'dine_in' && !selectedTable) return toast.error('Selecciona una mesa');
    if (orderType === 'delivery' && !customerPhone) return toast.error('Ingresa el teléfono');

    // Build full address for delivery
    let fullAddress = '';
    if (orderType === 'delivery') {
      fullAddress = [customerStreet, customerNumber, customerFloor, customerNeighborhood]
        .filter(Boolean).join(', ');
    }

    // Store the current order type and table before async operations
    const isDineIn = orderType === 'dine_in';
    const currentTable = selectedTable;
    
    // Set ref BEFORE any state changes to ensure panel stays open
    if (isDineIn && currentTable) {
      keepPanelOpenRef.current = true;
    }

    setProcessing(true);
    try {
      // Register or update customer with CRM fields - check for duplicates first
      const customerResult = await registerOrUpdateCustomer(skipDuplicateCheck);
      
      // If duplicate found, pause order creation and show modal
      if (customerResult === 'DUPLICATE_FOUND') {
        setProcessing(false);
        setPendingOrderCreation('handleCreateOrder');
        return; // Modal will handle the rest
      }
      
      const response = await api.post('/orders', {
        items: activeItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          // For weight-based products, include weight info
          sold_by_weight: item.sold_by_weight || false,
          weight_quantity: item.weight_quantity || null,
          unit_price: item.sold_by_weight ? item.price : null,
          // For products with selectable ingredients
          selected_ingredients: item.selected_ingredients || null,
          // Item notes/special instructions
          notes: item.item_notes || null
        })),
        order_type: orderType,
        table_id: selectedTable?.table_id || null,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        customer_address: fullAddress || customerAddress || null,
        num_guests: parseInt(numGuests) || 1,
        comments: comments || null,
        discount_amount: discountAmount || 0,
        discount_reason: discountAmount > 0 ? `Descuento $${discountAmount}` : null,
        auto_complete: false,
        branch_id: branchId  // Sucursal donde se realiza la venta
      });
      
      toast.success(`Orden #${response.data.order_number} creada`);
      
      // Clear cart and form fields but DON'T touch panel/table state
      setCart([]);
      setSearchQuery('');
      setCustomerName('');
      setCustomerPhone('');
      setCustomerAddress('');
      setCustomerStreet('');
      setCustomerNumber('');
      setCustomerFloor('');
      setCustomerNeighborhood('');
      setCustomerColonia('');
      setCustomerBirthday('');
      setSelectedCustomer(null);
      setNumGuests('');
      setComments('');
      setDeliveryDriver('');
      setEstimatedTime('');
      setDeliveryCost('');
      setDiscountAmount(0);
      setDiscountType('fixed');
      setShowDiscountInput(false);
      setIsReturningCustomer(false);
      setCustomerSuggestions([]);
      setShowCustomerSuggestions(false);
      
      // Fetch updated orders
      const ordersRes = await api.get('/orders');
      setAllOrders(ordersRes.data);
      
      // For dine_in, ensure panel stays open for next order
      if (isDineIn && currentTable) {
        // Force these states in the same render cycle
        setSelectedTable(currentTable);
        setShowNewOrderPanel(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la orden');
      keepPanelOpenRef.current = false;
    } finally {
      setProcessing(false);
    }
  };

  // Create order and mark as PREPARING (Marcar Listo - goes to kitchen monitor)
  const handleCreateAndMarkReady = async (skipDuplicateCheck = false) => {
    const activeItems = cart.filter(item => !item.cancelled);
    if (activeItems.length === 0) return toast.error('Agrega productos al pedido');

    setProcessing(true);
    try {
      // Register or update customer with CRM fields - check for duplicates first
      const customerResult = await registerOrUpdateCustomer(skipDuplicateCheck);
      
      // If duplicate found, pause order creation and show modal
      if (customerResult === 'DUPLICATE_FOUND') {
        setProcessing(false);
        setPendingOrderCreation('handleCreateAndMarkReady');
        return; // Modal will handle the rest
      }
      
      // First create the order as pending
      const response = await api.post('/orders', {
        items: activeItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          sold_by_weight: item.sold_by_weight || false,
          weight_quantity: item.weight_quantity || null,
          unit_price: item.sold_by_weight ? item.price : null,
          selected_ingredients: item.selected_ingredients || null,
          notes: item.item_notes || null
        })),
        order_type: orderType,
        table_id: null,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        customer_address: null,
        num_guests: 1,
        comments: comments || null,
        discount_amount: discountAmount || 0,
        discount_reason: discountAmount > 0 ? `Descuento $${discountAmount}` : null,
        auto_complete: false,
        branch_id: branchId  // Sucursal
      });
      
      // Mark as preparing so it appears in Kitchen Monitor
      await api.put(`/orders/${response.data.order_id}/status?status=preparing`);
      
      toast.success(`Orden #${response.data.order_number} enviada a cocina!`);
      clearForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la orden');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayTable = async (table) => {
    const tableOrders = dineInOrders.filter(o => 
      o.table_id === table.table_id && 
      ['pending', 'preparing', 'ready'].includes(o.status)
    );
    
    if (tableOrders.length === 0) {
      toast.error('No hay órdenes pendientes en esta mesa');
      return;
    }

    // Collect all items from all orders
    const allItems = [];
    tableOrders.forEach(order => {
      order.items?.forEach(item => {
        if (item.item_status !== 'cancelled') {
          allItems.push({
            ...item,
            order_id: order.order_id,
            order_number: order.order_number
          });
        }
      });
    });

    const totalToPay = tableOrders.reduce((sum, o) => sum + (o.total || 0), 0);
    
    // Open FUDO-style close modal
    setCloseTableData({
      table,
      orders: tableOrders,
      items: allItems,
      subtotal: totalToPay,
      total: totalToPay
    });
    setCloseTipAmount('');
    setCloseTipMethod('efectivo');
    setClosePaymentMethod('efectivo');
    setClosePaymentAmount(''); // Campo limpio para que el usuario ingrese con cuánto paga
    setClosePartialPayment(false);
    setCloseTableModalOpen(true);
  };

  // Process close table from modal
  const processCloseTable = async () => {
    if (!closeTableData) return;
    
    const tipValue = parseFloat(closeTipAmount) || 0;
    const paymentValue = parseFloat(closePaymentAmount) || 0;
    const totalWithTip = closeTableData.total + tipValue;
    
    if (paymentValue < totalWithTip && !closePartialPayment) {
      toast.error('El monto de pago es menor al total');
      return;
    }

    setProcessing(true);
    try {
      for (const order of closeTableData.orders) {
        await api.put(`/orders/${order.order_id}/status?status=completed&payment_method=${closePaymentMethod}`);
      }
      
      const change = paymentValue - totalWithTip;
      const label = closeTableData.isOrder 
        ? `Pedido #${closeTableData.table.number}` 
        : `Mesa ${closeTableData.table.number}`;
      
      toast.success(`${label} cobrado! Total: $${totalWithTip.toFixed(2)}${change > 0 ? ` - Cambio: $${change.toFixed(2)}` : ''}`);
      setCloseTableModalOpen(false);
      setCloseTableData(null);
      
      // Clear selections based on order type
      if (closeTableData.isOrder) {
        setSelectedTakeoutOrder(null);
        setSelectedDeliveryOrder(null);
      } else {
        setSelectedTable(null);
      }
      
      fetchData();
    } catch (error) {
      toast.error('Error al cobrar');
    } finally {
      setProcessing(false);
    }
  };

  // Open close modal for single order (Mostrador/Domicilio)
  const openCloseOrderModal = (order) => {
    const items = order.items?.filter(item => item.item_status !== 'cancelled') || [];
    const subtotal = order.subtotal || order.total || 0;
    const discount = order.discount_amount || 0;
    const total = order.total || (subtotal - discount);
    
    setCloseTableData({
      table: { number: order.order_number }, // Use order number as "table"
      orders: [order],
      items: items.map(item => ({
        ...item,
        order_id: order.order_id,
        order_number: order.order_number
      })),
      subtotal: subtotal,
      discount: discount,
      total: total,
      isOrder: true, // Flag to know it's a single order, not a table
      orderType: order.order_type
    });
    setCloseTipAmount('');
    setCloseTipMethod('efectivo');
    setClosePaymentMethod('efectivo');
    setClosePaymentAmount(''); // Campo limpio para que el usuario ingrese con cuánto paga
    setClosePartialPayment(false);
    setCloseTableModalOpen(true);
  };

  const [updatingStatus, setUpdatingStatus] = useState(false);

  const updateOrderStatus = async (orderId, newStatus) => {
    if (updatingStatus) return; // Prevent double clicks
    setUpdatingStatus(true);
    
    try {
      await api.put(`/orders/${orderId}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      
      // Si la orden se completó, limpiar el panel y preparar para nueva orden
      if (newStatus === 'completed') {
        // Limpiar el pedido seleccionado según el tipo
        setSelectedTakeoutOrder(null);
        setSelectedDeliveryOrder(null);
        setShowNewOrderPanel(false);
        // Reset form for new order
        setCustomerName('');
        setCustomerPhone('');
        setCustomerAddress('');
        setComments('');
      }
      
      await fetchOrders();
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('es-MX', { 
      timeZone: 'America/Hermosillo',
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getTimeElapsed = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMins = Math.floor((now - created) / 60000);
    if (diffMins < 60) return `${diffMins}m`;
    return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
  };

  // Filter orders by type
  const takeoutOrders = allOrders.filter(o => o.order_type === 'takeout');
  const deliveryOrders = allOrders.filter(o => o.order_type === 'delivery');
  const dineInOrders = allOrders.filter(o => o.order_type === 'dine_in');

  // Group by status
  const takeoutPending = takeoutOrders.filter(o => o.status === 'pending');
  const takeoutPreparing = takeoutOrders.filter(o => o.status === 'preparing');
  const takeoutReady = takeoutOrders.filter(o => o.status === 'ready');
  const takeoutCompleted = takeoutOrders.filter(o => o.status === 'completed').slice(0, 5);

  const deliveryPending = deliveryOrders.filter(o => o.status === 'pending');
  const deliveryPreparing = deliveryOrders.filter(o => o.status === 'preparing');
  const deliveryReady = deliveryOrders.filter(o => o.status === 'ready');
  const deliverySent = deliveryOrders.filter(o => o.status === 'sent');
  const deliveryCompleted = deliveryOrders.filter(o => o.status === 'completed').slice(0, 5);

  // Get table status
  const getTableStatus = (table) => {
    const hasActiveOrders = dineInOrders.some(o => 
      o.table_id === table.table_id && 
      ['pending', 'preparing', 'ready'].includes(o.status)
    );
    return hasActiveOrders ? 'occupied' : 'available';
  };

  const getTableOrders = (table) => {
    return dineInOrders.filter(o => 
      o.table_id === table.table_id && 
      ['pending', 'preparing', 'ready'].includes(o.status)
    );
  };

  const getTableTotal = (table) => {
    const orders = getTableOrders(table);
    return orders.reduce((sum, o) => sum + (o.total || 0), 0);
  };

  // Check if table has any split orders (cuentas divididas)
  const tableHasSplitOrders = (table) => {
    const orders = getTableOrders(table);
    return orders.some(o => o.split_from);
  };

  // Get count of orders on a table
  const getTableOrderCount = (table) => {
    return getTableOrders(table).length;
  };

  // Limpiar mesas fantasma
  const cleanupGhostTables = async () => {
    try {
      const res = await api.post('/orders/cleanup-ghost-tables');
      toast.success(res.data.message || 'Mesas limpiadas');
      fetchData();
    } catch (error) {
      toast.error('Error al limpiar mesas');
    }
  };

  // Cleanup ghost orders for Mostrador/Domicilio
  const cleanupGhostOrders = async (orderTypeToClean) => {
    try {
      const res = await api.post(`/orders/cleanup-ghost-orders?order_type=${orderTypeToClean}`);
      toast.success(res.data.message || 'Pedidos fantasma eliminados');
      fetchData();
    } catch (error) {
      toast.error('Error al limpiar pedidos fantasma');
    }
  };

  // ==================== MESAS VIEW ====================
  const renderMesasView = () => {
    const filteredTables = tables.filter(t => (t.area || 'Principal') === selectedArea);
    const currentTableOrders = selectedTable ? getTableOrders(selectedTable) : [];
    const currentTableTotal = selectedTable ? getTableTotal(selectedTable) : 0;

    return (
      <div className="flex flex-col md:flex-row h-full">
        {/* Left Panel - Table Map */}
        <div className={`flex-1 p-3 md:p-4 overflow-auto ${selectedTable ? 'hidden md:block' : ''}`}>
          {/* Area Tabs */}
          <div className="flex gap-2 mb-4 border-b pb-2 overflow-x-auto items-center">
            {tableAreas.map(area => (
              <button
                key={area}
                onClick={() => setSelectedArea(area)}
                className={`px-3 md:px-4 py-2 text-sm font-medium rounded-t transition-colors whitespace-nowrap ${
                  selectedArea === area 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {area} <span className="ml-1 text-xs opacity-75">{tables.filter(t => (t.area || 'Principal') === area).length}</span>
              </button>
            ))}
            {/* Botón limpiar mesas fantasma */}
            <button
              onClick={cleanupGhostTables}
              className="ml-auto px-3 py-2 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-lg flex items-center gap-1"
              title="Limpiar mesas fantasma"
            >
              <Trash2 size={14} /> Limpiar Fantasmas
            </button>
          </div>

          {/* Table Grid - Responsive */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2 md:gap-3">
            {filteredTables.map(table => {
              const status = getTableStatus(table);
              const isSelected = selectedTable?.table_id === table.table_id;
              const tableTotal = getTableTotal(table);
              const hasSplit = tableHasSplitOrders(table);
              const orderCount = getTableOrderCount(table);
              
              return (
                <button
                  key={table.table_id}
                  onClick={() => {
                    setSelectedTable(table);
                    setShowNewOrderPanel(false);
                  }}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center text-white font-bold transition-all active:scale-95 md:hover:scale-105 shadow-md relative ${
                    status === 'occupied' 
                      ? 'bg-gradient-to-br from-red-500 to-red-600' 
                      : 'bg-gradient-to-br from-green-500 to-green-600'
                  } ${isSelected ? 'ring-4 ring-orange-400 ring-offset-2' : ''} ${hasSplit ? 'ring-2 ring-purple-400' : ''}`}
                  data-testid={`table-${table.number}`}
                >
                  {/* Split order badge */}
                  {hasSplit && (
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-purple-600 rounded text-[7px] font-bold animate-pulse shadow-lg">
                      DIVIDIDA
                    </span>
                  )}
                  {/* Multiple orders badge */}
                  {orderCount > 1 && !hasSplit && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 rounded-full text-[10px] font-bold flex items-center justify-center shadow-lg">
                      {orderCount}
                    </span>
                  )}
                  <span className="text-xl md:text-2xl">{table.number}</span>
                  {status === 'occupied' && tableTotal > 0 && (
                    <span className="text-[10px] md:text-xs opacity-90">${tableTotal.toFixed(0)}</span>
                  )}
                </button>
              );
            })}
          </div>

          {filteredTables.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Utensils className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay mesas en esta área</p>
            </div>
          )}
        </div>

        {/* Right Panel - Table Details / Add Products - Full screen on mobile */}
        <div className={`${selectedTable ? 'fixed inset-0 z-40 md:relative md:inset-auto' : 'hidden md:block'} w-full md:w-96 border-l bg-white relative`} style={{display: 'flex', flexDirection: 'column', height: selectedTable ? '100dvh' : 'auto', paddingTop: selectedTable ? '56px' : '0'}}>
          {selectedTable ? (
            <>
              {/* Table Header */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold">MESA {selectedTable.number}</h2>
                    {selectedTable.area && (
                      <p className="text-sm text-red-200">{selectedTable.area}</p>
                    )}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-white hover:bg-red-700 h-10 w-10"
                    onClick={() => setSelectedTable(null)}
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>
                <Button 
                  size="default" 
                  className="mt-3 w-full md:w-auto bg-white text-red-600 hover:bg-gray-100 font-semibold"
                  onClick={() => {
                    // Auto-fill customer data from existing orders on this table
                    if (currentTableOrders.length > 0) {
                      const existingOrder = currentTableOrders[0];
                      if (existingOrder.customer_name) setCustomerName(existingOrder.customer_name);
                      if (existingOrder.customer_phone) setCustomerPhone(existingOrder.customer_phone);
                      if (existingOrder.num_guests) setNumGuests(existingOrder.num_guests.toString());
                    }
                    setShowNewOrderPanel(true);
                  }}
                >
                  + Añadir venta
                </Button>
              </div>

              {/* Current Orders - FUDO Style */}
              {currentTableOrders.length > 0 && !showNewOrderPanel && !showAddToOrderPanel && (
                <div className="flex-1 overflow-y-auto" style={{WebkitOverflowScrolling: 'touch'}}>
                  {currentTableOrders.map((order, orderIdx) => (
                    <div key={order.order_id} className="border-b">
                      {/* Order Header */}
                      <div className="p-3 bg-gray-100 flex justify-between items-center sticky top-0 z-10">
                        <div>
                          <span className="font-bold">VENTA #{order.order_number}</span>
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs text-white ${ORDER_STATUSES[order.status]?.color}`}>
                            {ORDER_STATUSES[order.status]?.label}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {order.status !== 'completed' && order.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              className="h-9 px-3 bg-orange-500 hover:bg-orange-600 text-white font-medium"
                              onClick={() => selectOrderForEditing(order)}
                              data-testid={`add-to-order-${order.order_id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Añadir
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Order Info - Simplified format */}
                      <div className="px-3 py-2 border-b bg-white">
                        <p className="text-xs text-gray-500">
                          <span>{order.num_guests || 1} personas</span>
                          <span className="mx-1">|</span>
                          <span>{formatDateTime(order.created_at)}</span>
                        </p>
                        {/* Customer Info */}
                        {(order.customer_name || order.customer_phone) && (
                          <div className="mt-1 flex items-center gap-2 text-sm">
                            <User className="w-4 h-4 text-blue-500" />
                            <span className="font-medium text-gray-700">{order.customer_name || 'Sin nombre'}</span>
                            {order.customer_phone && (
                              <span className="text-gray-500">• {order.customer_phone}</span>
                            )}
                            {canEditOrders && order.status !== 'completed' && order.status !== 'voided' && (
                              <button
                                onClick={() => openEditCustomerModal(order)}
                                className="ml-auto p-1 text-blue-500 hover:bg-blue-50 rounded"
                                title="Editar información del cliente"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )}
                        {/* Add customer info button when no customer data */}
                        {!order.customer_name && !order.customer_phone && canEditOrders && order.status !== 'completed' && order.status !== 'voided' && (
                          <button
                            onClick={() => openEditCustomerModal(order)}
                            className="mt-1 flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700"
                          >
                            <UserPlus className="w-3 h-3" />
                            <span>Agregar cliente</span>
                          </button>
                        )}
                      </div>

                      {/* Order Items - Scrollable */}
                      <div className="p-3">
                        <div className="space-y-2">
                          {order.items?.map((item, idx) => {
                            const isCancelled = item.item_status === 'cancelled';
                            return (
                              <div 
                                key={idx} 
                                className={`flex justify-between items-center text-sm py-2 ${
                                  isCancelled ? 'line-through text-gray-400 bg-red-50 rounded px-2' : ''
                                }`}
                                data-testid={`order-item-${order.order_id}-${idx}`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
                                    isCancelled ? 'bg-red-100 text-red-500' :
                                    item.item_status === 'ready' ? 'bg-green-100 text-green-700' :
                                    item.item_status === 'preparing' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {isCancelled ? <XCircle className="w-4 h-4" /> : item.quantity}
                                  </span>
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                      {item.is_promo_item && (
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500 text-white font-bold">
                                          🏷️ PROMO
                                        </span>
                                      )}
                                      <span>{item.product_name}</span>
                                    </div>
                                    {item.is_promo_item && item.promo_name && (
                                      <span className="text-xs text-purple-600">
                                        {item.promo_name}
                                      </span>
                                    )}
                                    {item.description && (
                                      <span className="text-xs text-amber-600">
                                        📋 {item.description}
                                      </span>
                                    )}
                                    {isCancelled && item.cancellation_reason && (
                                      <span className="text-xs text-red-500 font-normal no-underline" style={{textDecoration: 'none'}}>
                                        Motivo: {item.cancellation_reason}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium ${isCancelled ? 'text-gray-400' : ''}`}>
                                    ${item.subtotal?.toFixed(2)}
                                  </span>
                                  {!isCancelled && order.status !== 'completed' && order.status !== 'cancelled' && (
                                    <button
                                      onClick={() => openCancelExistingItemModal(order.order_id, idx, item.product_name)}
                                      className="w-6 h-6 bg-red-100 text-red-600 rounded flex items-center justify-center hover:bg-red-200 transition-colors"
                                      title="Cancelar item"
                                      data-testid={`cancel-existing-item-${order.order_id}-${idx}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Order Total & Actions */}
                      <div className="p-3 border-t bg-gray-50">
                        {order.discount_amount > 0 && (
                          <div className="space-y-1 mb-2 text-sm">
                            <div className="flex justify-between text-gray-600">
                              <span>Subtotal:</span>
                              <span>${order.subtotal?.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-green-600" data-testid={`table-discount-line-${order.order_id}`}>
                              <span>Descuento:</span>
                              <span>-${order.discount_amount?.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-lg mb-2">
                          <span>Total</span>
                          <span>${order.total?.toFixed(2)}</span>
                        </div>
                        <div className="flex gap-2">
                          {order.items?.length > 1 && order.status !== 'completed' && (
                            <button
                              onClick={() => openSplitModal(order)}
                              className="flex-1 flex items-center justify-center gap-1 text-xs text-blue-600 hover:bg-blue-50 py-1.5 rounded border border-blue-200"
                              data-testid={`split-bill-${order.order_id}`}
                            >
                              <Users className="w-3 h-3" />
                              Dividir
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add to Existing Order Panel - Full overlay on mobile */}
              {showAddToOrderPanel && selectedOrder && (
                <div className="absolute inset-0 md:relative md:flex-1 flex flex-col bg-white z-20 overflow-hidden">
                  {/* Selected Order Info - Compact */}
                  <div className="p-2 bg-orange-500 text-white">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-sm">AÑADIR A VENTA #{selectedOrder.order_number}</span>
                      <Button variant="ghost" size="icon" className="text-white hover:bg-orange-600" onClick={() => {
                        setShowAddToOrderPanel(false);
                        setSelectedOrder(null);
                        setCart([]);
                      }}>
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>

                  {/* Existing Items - Very compact on mobile */}
                  <div className="p-2 border-b max-h-16 md:max-h-24 overflow-auto bg-gray-50">
                    <div className="text-xs font-bold text-gray-500 mb-1">ACTUALES ({selectedOrder.items?.length || 0})</div>
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-xs py-0.5">
                        <span>{item.quantity}x {item.product_name}</span>
                        <span className="text-gray-500">${item.subtotal?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Category Tabs */}
                  <div className="p-2 border-b overflow-x-auto bg-gray-50">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setSelectedCategory('all')}
                        className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                          selectedCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-100 border'
                        }`}
                      >
                        Todos
                      </button>
                      {flatCategories.map(cat => (
                        <button
                          key={cat.category_id}
                          onClick={() => setSelectedCategory(cat.category_id)}
                          className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                            selectedCategory === cat.category_id ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-100 border'
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Product Search */}
                  <div className="p-2 border-b">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Buscar producto..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9 h-9 text-sm"
                          data-testid="add-to-order-search"
                        />
                      </div>
                      <button
                        onClick={openCustomProductModal}
                        className="text-xs bg-purple-500 text-white px-2 py-2 rounded hover:bg-purple-600 flex items-center gap-1 whitespace-nowrap"
                        data-testid="add-custom-product-table-btn"
                      >
                        <Star className="w-3 h-3" />
                      </button>
                    </div>
                    
                    {/* Product List - Large on mobile */}
                    <div className="h-[35vh] md:max-h-48 overflow-auto border rounded bg-white">
                      {filteredProducts.slice(0, 30).map(product => (
                        <button
                          key={product.product_id}
                          onClick={() => addToCart(product)}
                          className="w-full text-left p-2 hover:bg-orange-50 active:bg-orange-100 text-sm border-b last:border-b-0"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium truncate block">{product.name}</span>
                              {product.description && (
                                <span className="text-xs text-gray-500 truncate block" dangerouslySetInnerHTML={{ __html: '→ ' + parseMarkdown(product.description) }} />
                              )}
                            </div>
                            <span className="text-orange-600 font-bold ml-2 whitespace-nowrap">${product.price?.toFixed(2)}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* New Items Cart */}
                  {cart.length > 0 && (
                    <div className="border-t p-2 bg-orange-50">
                      <div className="text-xs font-bold text-orange-600 mb-1">NUEVOS ({cart.length})</div>
                      <div className="space-y-1 max-h-20 md:max-h-24 overflow-auto">
                        {cart.map((item, idx) => (
                          <div key={`${item.product_id}-${idx}`} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <button onClick={() => updateQuantity(item.product_id, -1)} className="w-6 h-6 bg-white rounded">-</button>
                              <span className="w-5 text-center">{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.product_id, 1)} className="w-6 h-6 bg-white rounded">+</button>
                              <div className="truncate max-w-20">
                                <span>{item.name}</span>
                                {item.sold_by_weight && <span className="text-xs text-blue-500 ml-1">({item.quantity} {item.weight_unit || 'kg'})</span>}
                                {item.item_notes && <p className="text-xs text-orange-600 truncate">📝 {item.item_notes}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span>${(item.sold_by_weight ? item.calculated_price : item.price * item.quantity).toFixed(2)}</span>
                              <button onClick={() => removeFromCart(item.product_id)} className="text-red-500">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 pt-2 border-t flex justify-between font-bold text-sm">
                        <span>Agregar:</span>
                        <span>${cart.reduce((sum, i) => sum + (i.sold_by_weight ? i.calculated_price : i.price * i.quantity), 0).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="p-3 border-t bg-white pb-20 md:pb-3">
                    <Button
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={handleAddToExistingOrder}
                      disabled={cart.length === 0 || processing}
                      data-testid="confirm-add-to-order"
                    >
                      {processing ? 'Agregando...' : 'Agregar a la Orden'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Add Products Panel - NEW ORDER for Tables */}
              {showNewOrderPanel && (
                <div className="absolute inset-0 md:relative md:flex-1 flex flex-col bg-white z-20 overflow-hidden">
                  {/* Header */}
                  <div className="bg-orange-500 text-white p-3 flex items-center justify-between">
                    <h3 className="font-bold text-sm">NUEVO PEDIDO - MESA {selectedTable?.number}</h3>
                    <Button variant="ghost" size="icon" className="text-white hover:bg-orange-600" onClick={() => setShowNewOrderPanel(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <div className="flex-1 overflow-auto">
                    {/* Customer Info - Enhanced with CRM fields */}
                    <div className="p-2 md:p-3 border-b bg-gray-50">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3 text-gray-400 hidden md:block" />
                          <Input
                            type="number"
                            min="1"
                            value={numGuests}
                            onChange={(e) => setNumGuests(e.target.value)}
                            placeholder="# pers"
                            className="h-8 text-sm"
                            data-testid="mesa-num-guests"
                          />
                        </div>
                        <div className="relative col-span-2">
                          <Input
                            value={customerName}
                            onChange={handleNameChange}
                            placeholder="Nombre del cliente"
                            className="h-8 text-sm"
                            data-testid="mesa-customer-name"
                            onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                          />
                          {showCustomerSuggestions && customerSuggestions.length > 0 && (
                            <div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-48 overflow-auto">
                              {customerSuggestions.map(customer => (
                                <button
                                  key={customer.customer_id}
                                  onClick={() => selectCustomer(customer)}
                                  className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b last:border-b-0"
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-medium text-sm">{customer.name}</p>
                                      <p className="text-xs text-gray-500">{customer.phone}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-xs text-green-600 font-medium">${customer.total_spent?.toFixed(0) || 0}</p>
                                      <p className="text-xs text-gray-400">{customer.visit_count || 0} visitas</p>
                                    </div>
                                  </div>
                                  {customer.quick_stats?.favorites?.length > 0 && (
                                    <p className="text-xs text-orange-500 mt-1 truncate">
                                      Favoritos: {customer.quick_stats.favorites.map(f => f.name).join(', ')}
                                    </p>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Phone and additional fields for new customer */}
                      {!selectedCustomer && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div className="relative">
                            <Input
                              value={customerPhone}
                              onChange={handlePhoneChange}
                              placeholder="Teléfono"
                              className="h-8 text-sm"
                              data-testid="mesa-customer-phone"
                            />
                          </div>
                          <div>
                            <Input
                              value={customerColonia}
                              onChange={(e) => setCustomerColonia(e.target.value)}
                              placeholder="Colonia"
                              className="h-8 text-sm"
                              data-testid="mesa-customer-colonia"
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Selected Customer Info Card - Enhanced */}
                      {selectedCustomer && (
                        <div className="mt-2 p-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {selectedCustomer.name?.charAt(0)?.toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{selectedCustomer.name}</p>
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {selectedCustomer.phone}
                                </p>
                              </div>
                            </div>
                            <button onClick={clearSelectedCustomer} className="text-gray-400 hover:text-red-500">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2 text-xs">
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                              Total: ${selectedCustomer.total_spent?.toFixed(0) || 0}
                            </span>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                              {selectedCustomer.visit_count || 0} visitas
                            </span>
                            {selectedCustomer.colonia && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {selectedCustomer.colonia}
                              </span>
                            )}
                            {selectedCustomer.birthday && (
                              <span className="px-2 py-0.5 bg-pink-100 text-pink-700 rounded-full">
                                🎂 {selectedCustomer.birthday}
                              </span>
                            )}
                          </div>
                          {selectedCustomer.last_visit && (
                            <p className="text-xs text-gray-500 mt-1">
                              Última visita: {new Date(selectedCustomer.last_visit).toLocaleDateString('es-MX')}
                            </p>
                          )}
                          {selectedCustomer.quick_stats?.favorites?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-orange-200">
                              <p className="text-xs text-gray-600 font-medium">Favoritos:</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {selectedCustomer.quick_stats.favorites.slice(0, 3).map((fav, idx) => (
                                  <span key={idx} className="text-xs bg-white px-2 py-0.5 rounded border">
                                    {fav.name} ({fav.count})
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Category Tabs */}
                    <div className="p-2 border-b overflow-x-auto bg-gray-50">
                      <div className="flex gap-1">
                        <button
                          onClick={() => setSelectedCategory('all')}
                          className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                            selectedCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-100 border'
                          }`}
                        >
                          Todos
                        </button>
                        {flatCategories.map(cat => (
                          <button
                            key={cat.category_id}
                            onClick={() => setSelectedCategory(cat.category_id)}
                            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                              selectedCategory === cat.category_id ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-100 border'
                            }`}
                          >
                            {cat.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Product Search */}
                    <div className="p-2 md:p-3 border-b">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            placeholder="Buscar producto..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 h-9 text-sm"
                            data-testid="product-search-input"
                          />
                        </div>
                        <button
                          onClick={openCustomProductModal}
                          className="text-xs bg-purple-500 text-white px-2 md:px-3 py-2 rounded hover:bg-purple-600 flex items-center gap-1"
                          data-testid="add-custom-product-mesa-btn"
                        >
                          <Star className="w-3 h-3" />
                        </button>
                      </div>
                      
                      {/* Product List - Large on mobile */}
                      <div className="h-[35vh] md:max-h-48 overflow-auto border rounded bg-white">
                        {filteredProducts.slice(0, 30).map(product => (
                          <button
                            key={product.product_id}
                            onClick={() => addToCart(product)}
                            className="w-full text-left p-2 hover:bg-orange-50 active:bg-orange-100 text-sm border-b last:border-b-0"
                            data-testid={`search-result-${product.product_id}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <span className="font-medium truncate block">{product.name}</span>
                                {product.description && (
                                  <span className="text-xs text-gray-500 truncate block" dangerouslySetInnerHTML={{ __html: '→ ' + parseMarkdown(product.description) }} />
                                )}
                              </div>
                              <span className="text-orange-600 font-bold ml-2 whitespace-nowrap">${product.price?.toFixed(2)}</span>
                            </div>
                          </button>
                        ))}
                        {filteredProducts.length === 0 && (
                          <p className="text-center py-4 text-gray-400 text-sm">Sin productos</p>
                        )}
                      </div>
                    </div>

                    {/* Cart */}
                    <div className="p-2 md:p-3">
                      {cart.length > 0 ? (
                        <div>
                          <Label className="text-xs text-gray-500 mb-2 block">PRODUCTOS ({cart.length})</Label>
                          <div className="space-y-1 max-h-24 md:max-h-32 overflow-auto">
                            {cart.map((item, cartIdx) => (
                              <div key={`${item.product_id}-${cartIdx}`} className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-sm">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => updateQuantity(item.product_id, -1)} className="w-6 h-6 bg-gray-200 rounded text-sm font-bold">-</button>
                                  <span className="font-bold w-5 text-center">{item.quantity}</span>
                                  <button onClick={() => updateQuantity(item.product_id, 1)} className="w-6 h-6 bg-gray-200 rounded text-sm font-bold">+</button>
                                  <span className="truncate max-w-[80px]">{item.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                                  <button onClick={() => removeFromCart(item.product_id)} className="text-red-500"><X className="w-4 h-4" /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-400">
                          <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Agrega productos</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer with Total and Button */}
                  <div className="border-t p-3 bg-white pb-20 md:pb-3">
                    {cart.length > 0 && (
                      <div className="flex justify-between mb-2 font-bold">
                        <span>Total:</span>
                        <span>${cart.reduce((sum, i) => sum + i.price * i.quantity, 0).toFixed(2)}</span>
                      </div>
                    )}
                    <Button
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={handleCreateOrder}
                      disabled={cart.length === 0 || processing}
                      data-testid="submit-table-order"
                    >
                      {processing ? 'Procesando...' : 'Crear Orden'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Footer Actions - Fixed at bottom, hidden when panels are open */}
              {!showNewOrderPanel && !showAddToOrderPanel && (
                <div className="p-4 border-t bg-gray-50 pb-20 md:pb-4 mt-auto flex-shrink-0">
                  {currentTableOrders.length > 0 ? (
                    <div className="space-y-2">
                      {/* Change Table Button */}
                      <Button 
                        variant="outline"
                        className="w-full border-blue-500 text-blue-500 hover:bg-blue-50" 
                        onClick={() => {
                          const order = currentTableOrders[0];
                          if (order) {
                            openChangeTableModal(order.order_id, selectedTable.number);
                          }
                        }}
                        disabled={processing}
                        data-testid="change-table-btn"
                      >
                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                        Cambiar Mesa
                      </Button>
                      {/* Discount Button for Tables */}
                      <Button 
                        variant="outline"
                        className="w-full border-orange-500 text-orange-500 hover:bg-orange-50" 
                        onClick={() => {
                          const order = currentTableOrders[0];
                          if (order) {
                            openDiscountModal({
                              ...order,
                              order_id: order.order_id,
                              subtotal: currentTableTotal,
                              total: currentTableTotal
                            });
                          }
                        }}
                        disabled={processing}
                        data-testid="table-discount-btn"
                      >
                        <Percent className="w-4 h-4 mr-2" />
                        Aplicar Descuento
                      </Button>
                      <Button 
                        className="w-full bg-gray-800 hover:bg-gray-900" 
                        onClick={() => handlePayTable(selectedTable)}
                        disabled={processing}
                      >
                        Cerrar Mesa {selectedTable.number} (${currentTableTotal.toFixed(2)})
                      </Button>
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 text-sm">Mesa disponible</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Utensils className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Selecciona una mesa</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ==================== ORDER TABLE COMPONENT ====================
  const OrderTable = ({ orders, title, color, showActions = true, showAddress = false }) => (
    <div className="mb-6">
      <h3 className={`font-bold text-sm mb-2 ${color} flex items-center gap-2`}>
        {title} ({orders.length})
      </h3>
      <div className="bg-white rounded border overflow-hidden">
        <table className="w-full text-sm">
          <thead className={`${color.replace('text-', 'bg-').replace('600', '100')}`}>
            <tr>
              <th className="text-left p-2">ID</th>
              <th className="text-left p-2">Hora Inicio</th>
              {showAddress && <th className="text-left p-2">Dirección</th>}
              <th className="text-left p-2">Cliente</th>
              <th className="text-left p-2">Estado</th>
              <th className="text-left p-2">Tiempo</th>
              <th className="text-right p-2">Total</th>
              {showActions && <th className="text-center p-2">Acción</th>}
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={showAddress ? 8 : 7} className="text-center py-4 text-gray-400">
                  Sin pedidos
                </td>
              </tr>
            ) : (
              orders.map(order => (
                <tr key={order.order_id} className="border-t hover:bg-gray-50">
                  <td className="p-2 font-mono font-bold">{order.order_number}</td>
                  <td className="p-2 text-xs">{formatDateTime(order.created_at)}</td>
                  {showAddress && (
                    <td className="p-2 text-xs max-w-32 truncate">{order.customer_address || '-'}</td>
                  )}
                  <td className="p-2">{order.customer_name || '-'}</td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs text-white ${ORDER_STATUSES[order.status]?.color}`}>
                      {ORDER_STATUSES[order.status]?.label}
                    </span>
                  </td>
                  <td className="p-2 text-xs">{getTimeElapsed(order.created_at)}</td>
                  <td className="p-2 text-right font-bold">${order.total?.toFixed(2)}</td>
                  {showActions && (
                    <td className="p-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {order.status === 'pending' && (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateOrderStatus(order.order_id, 'preparing')}>
                              Preparar
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => openVoidOrderModal(order.order_id, order.order_number)}
                              data-testid={`void-order-${order.order_id}`}
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        {order.status === 'preparing' && (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateOrderStatus(order.order_id, 'ready')}>
                              Listo
                            </Button>
                            {canVoidOrders && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => openVoidOrderModal(order.order_id, order.order_number)}
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                            )}
                          </>
                        )}
                        {order.status === 'ready' && orderType === 'takeout' && (
                          <>
                            <Button size="sm" className="h-7 text-xs bg-green-500" onClick={() => openCloseOrderModal(order)}>
                              Cobrar
                            </Button>
                            {canVoidOrders && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => openVoidOrderModal(order.order_id, order.order_number)}
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                            )}
                          </>
                        )}
                        {order.status === 'ready' && orderType === 'delivery' && (
                          <>
                            <Button size="sm" className="h-7 text-xs bg-purple-500" onClick={() => updateOrderStatus(order.order_id, 'sent')}>
                              Enviar
                            </Button>
                            {canVoidOrders && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => openVoidOrderModal(order.order_id, order.order_number)}
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                            )}
                          </>
                        )}
                        {order.status === 'sent' && (
                          <>
                            <Button size="sm" className="h-7 text-xs bg-green-500" onClick={() => openCloseOrderModal(order)}>
                              Cobrar
                            </Button>
                            {canVoidOrders && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => openVoidOrderModal(order.order_id, order.order_number)}
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ==================== MOSTRADOR (PARA LLEVAR) VIEW ====================
  const renderMostradorView = () => {
    const currentOrder = selectedTakeoutOrder;
    const allTakeoutOrders = [...takeoutPending, ...takeoutPreparing, ...takeoutReady];
    
    // Calculate stats for today
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayCompleted = takeoutCompleted.filter(o => new Date(o.created_at) >= todayStart);
    const todaySales = todayCompleted.reduce((sum, o) => sum + (o.total || 0), 0);
    
    return (
      <div className="flex flex-col h-full">
        {/* Stats Header Bar */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              <span className="font-bold text-lg">MOSTRADOR</span>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-lg">
                <DollarSign className="w-4 h-4" />
                <span className="font-semibold">${todaySales.toFixed(0)}</span>
                <span className="text-xs opacity-80">hoy</span>
              </div>
              <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-lg">
                <Clock className="w-4 h-4" />
                <span className="font-semibold">{allTakeoutOrders.length}</span>
                <span className="text-xs opacity-80">activas</span>
              </div>
              <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-lg">
                <CheckCircle className="w-4 h-4" />
                <span className="font-semibold">{todayCompleted.length}</span>
                <span className="text-xs opacity-80">cerradas</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => cleanupGhostOrders('takeout')}
              className="px-3 py-2 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-lg flex items-center gap-1"
              title="Limpiar pedidos fantasma de mostrador"
              data-testid="cleanup-ghost-takeout-btn"
            >
              <Trash2 size={14} /> Limpiar Fantasmas
            </button>
            <Button 
              className="bg-white text-orange-600 hover:bg-orange-50 font-bold shadow-lg"
              onClick={() => {
                clearForm();
                setOrderType('takeout');
                setSelectedTakeoutOrder(null);
                setShowNewOrderPanel(true);
              }}
              data-testid="new-takeout-order-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Nuevo Pedido
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Order Cards */}
          <div className={`flex-1 p-4 overflow-auto bg-gray-50 ${currentOrder ? 'hidden md:block' : ''}`}>
            {/* Search */}
            <div className="mb-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Buscar por cliente o #orden..." 
                  className="pl-9 bg-white"
                  value={orderSearchQuery}
                  onChange={(e) => setOrderSearchQuery(e.target.value)}
                  data-testid="mostrador-order-search"
                />
              </div>
            </div>

            {/* Order Cards by Status */}
            {allTakeoutOrders.length > 0 ? (
              <div className="space-y-6">
                {/* Pending */}
                {takeoutPending.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse"></div>
                      <span className="text-sm font-bold text-gray-600">PENDIENTES ({takeoutPending.length})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {takeoutPending.filter(o => !orderSearchQuery || o.order_number?.toString().includes(orderSearchQuery.toLowerCase()) || o.customer_name?.toLowerCase().includes(orderSearchQuery.toLowerCase())).map(order => (
                        <button key={order.order_id} onClick={() => { setSelectedTakeoutOrder(order); setShowNewOrderPanel(false); }}
                          className={`p-4 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 text-white text-left shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all ${currentOrder?.order_id === order.order_id ? 'ring-4 ring-amber-300 ring-offset-2' : ''} ${order.split_from ? 'ring-2 ring-purple-400' : ''}`}
                          data-testid={`takeout-order-${order.order_id}`}>
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-1">
                              <span className="text-2xl font-black">#{order.order_number}</span>
                              {order.split_from && <span className="px-1.5 py-0.5 bg-purple-600 rounded text-[8px] font-bold animate-pulse">DIVIDIDA</span>}
                            </div>
                            <span className="px-2 py-0.5 bg-white/25 rounded-full text-[10px] font-bold">{getTimeElapsed(order.created_at)}</span>
                          </div>
                          <p className="font-semibold mt-2 truncate">{order.customer_name || 'Sin nombre'}</p>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xl font-black">${order.total?.toFixed(2)}</span>
                            <span className="text-xs opacity-75">{order.items?.length || 0} items</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preparing */}
                {takeoutPreparing.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse"></div>
                      <span className="text-sm font-bold text-gray-600">EN PREPARACIÓN ({takeoutPreparing.length})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {takeoutPreparing.filter(o => !orderSearchQuery || o.order_number?.toString().includes(orderSearchQuery.toLowerCase()) || o.customer_name?.toLowerCase().includes(orderSearchQuery.toLowerCase())).map(order => (
                        <button key={order.order_id} onClick={() => { setSelectedTakeoutOrder(order); setShowNewOrderPanel(false); }}
                          className={`p-4 rounded-xl bg-gradient-to-br from-blue-400 to-blue-500 text-white text-left shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all ${currentOrder?.order_id === order.order_id ? 'ring-4 ring-blue-300 ring-offset-2' : ''}`}
                          data-testid={`takeout-order-${order.order_id}`}>
                          <div className="flex justify-between items-start">
                            <span className="text-2xl font-black">#{order.order_number}</span>
                            <span className="px-2 py-0.5 bg-white/25 rounded-full text-[10px] font-bold flex items-center gap-1"><ChefHat className="w-3 h-3" />{getTimeElapsed(order.created_at)}</span>
                          </div>
                          <p className="font-semibold mt-2 truncate">{order.customer_name || 'Sin nombre'}</p>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xl font-black">${order.total?.toFixed(2)}</span>
                            <span className="text-xs opacity-75">{order.items?.length || 0} items</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ready */}
                {takeoutReady.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-sm font-bold text-gray-600">LISTAS PARA ENTREGAR ({takeoutReady.length})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {takeoutReady.filter(o => !orderSearchQuery || o.order_number?.toString().includes(orderSearchQuery.toLowerCase()) || o.customer_name?.toLowerCase().includes(orderSearchQuery.toLowerCase())).map(order => (
                        <button key={order.order_id} onClick={() => { setSelectedTakeoutOrder(order); setShowNewOrderPanel(false); }}
                          className={`p-4 rounded-xl bg-gradient-to-br from-green-400 to-green-500 text-white text-left shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all ${currentOrder?.order_id === order.order_id ? 'ring-4 ring-green-300 ring-offset-2' : ''}`}
                          data-testid={`takeout-order-${order.order_id}`}>
                          <div className="flex justify-between items-start">
                            <span className="text-2xl font-black">#{order.order_number}</span>
                            <span className="px-2 py-0.5 bg-white/25 rounded-full text-[10px] font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" />LISTO</span>
                          </div>
                          <p className="font-semibold mt-2 truncate">{order.customer_name || 'Sin nombre'}</p>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xl font-black">${order.total?.toFixed(2)}</span>
                            <span className="px-2 py-0.5 bg-white/30 rounded text-xs font-bold">COBRAR</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : !showNewOrderPanel && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Package className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg font-medium text-gray-500">No hay pedidos activos</p>
                <p className="text-sm">Haz clic en "Nuevo Pedido" para crear uno</p>
              </div>
            )}

            {/* Completed Orders - Compact History */}
            {todayCompleted.length > 0 && (
              <div className="mt-8 pt-6 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-600 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    HISTORIAL DE HOY ({todayCompleted.length})
                  </h3>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                  {todayCompleted.slice(0, 16).map(order => (
                    <div key={order.order_id} className="p-2 rounded-lg bg-white border hover:border-gray-300 transition-colors cursor-pointer" onClick={() => setSelectedTakeoutOrder(order)}>
                      <div className="font-bold text-gray-800">#{order.order_number}</div>
                      <div className="text-xs text-gray-500 truncate">{order.customer_name || '-'}</div>
                      <div className="text-sm font-semibold text-green-600">${order.total?.toFixed(2)}</div>
                      <div className="text-[10px] text-gray-400">{new Date(order.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Hermosillo' })}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>

        {/* Right Panel - Order Details / New Order */}
        <div className={`${currentOrder || showNewOrderPanel ? 'fixed inset-0 z-40 pt-14 md:pt-0 md:relative md:inset-auto' : 'hidden md:block'} w-full md:w-96 border-l bg-white flex flex-col`} style={{maxHeight: (currentOrder || showNewOrderPanel) ? 'calc(100vh - 56px)' : 'auto'}}>
          {currentOrder ? (
            <>
              {/* Order Header */}
              <div className={`${ORDER_STATUSES[currentOrder.status]?.color || 'bg-orange-500'} text-white p-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold">PEDIDO #{currentOrder.order_number}</h2>
                    <p className="text-sm opacity-90">{currentOrder.customer_name || 'Sin nombre'}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-white hover:bg-white/20 h-10 w-10"
                    onClick={() => setSelectedTakeoutOrder(null)}
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>
                <Button 
                  size="default" 
                  className="mt-3 w-full md:w-auto bg-white text-orange-600 hover:bg-gray-100 font-semibold"
                  onClick={() => {
                    selectOrderForEditing(currentOrder);
                    setShowNewOrderPanel(false);
                  }}
                >
                  + Añadir productos
                </Button>
              </div>

              {/* Order Info */}
              <div className="px-4 py-2 text-xs text-gray-500 border-b flex items-center gap-2">
                <Clock className="w-3 h-3" />
                {formatDateTime(currentOrder.created_at)}
                {currentOrder.customer_phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {currentOrder.customer_phone}
                  </span>
                )}
              </div>

              {/* Add to Order Panel */}
              {showAddToOrderPanel && selectedOrder?.order_id === currentOrder.order_id ? (
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Search with Categories */}
                  <div className="p-3 bg-gray-800 text-white">
                    <span className="font-bold text-sm">ADICIONAR PRODUCTOS</span>
                  </div>
                  
                  {/* Category Tabs */}
                  <div className="p-2 border-b overflow-x-auto">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setSelectedCategory('all')}
                        className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                          selectedCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        Todos
                      </button>
                      {flatCategories.map(cat => (
                        <button
                          key={cat.category_id}
                          onClick={() => setSelectedCategory(cat.category_id)}
                          className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                            selectedCategory === cat.category_id ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Product Search */}
                  <div className="p-3 border-b">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-500 rounded flex items-center justify-center">
                        <Plus className="w-5 h-5 text-white" />
                      </div>
                      <div className="relative flex-1">
                        <Input
                          placeholder="Buscar producto"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="bg-gray-100"
                          data-testid="takeout-add-product-search"
                        />
                      </div>
                      <button
                        onClick={openCustomProductModal}
                        className="text-xs bg-purple-500 text-white px-3 py-2 rounded hover:bg-purple-600 flex items-center gap-1 whitespace-nowrap"
                        data-testid="takeout-add-custom-product-btn"
                      >
                        <Star className="w-3 h-3" /> Especial
                      </button>
                    </div>
                    
                    {/* Search Results */}
                    {searchQuery.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-auto border rounded">
                        {filteredProducts.slice(0, 10).map(product => (
                          <button
                            key={product.product_id}
                            onClick={() => addToCart(product)}
                            className="w-full text-left p-2 hover:bg-orange-50 text-sm flex justify-between border-b"
                          >
                            <span>{product.name}</span>
                            <span className="text-gray-500">${product.price?.toFixed(2)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Cart for Adding */}
                  <div className="flex-1 overflow-auto p-3 min-h-0" style={{WebkitOverflowScrolling: 'touch'}}>
                    {cart.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-xs font-bold text-gray-500 mb-2">PRODUCTOS A AGREGAR</div>
                        {cart.map((item, cartIdx) => (
                          <div key={`${item.product_id}-${cartIdx}`} className="flex items-center justify-between py-2 border-b">
                            <div className="flex items-center gap-2">
                              <button onClick={() => updateQuantity(item.product_id, -1)} className="w-6 h-6 bg-gray-200 rounded text-xs">-</button>
                              <span className="font-bold">{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.product_id, 1)} className="w-6 h-6 bg-gray-200 rounded text-xs">+</button>
                              <span className="text-sm">{item.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                              <button onClick={() => removeFromCart(item.product_id)} className="text-red-500">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Busca productos para agregar</p>
                      </div>
                    )}
                  </div>

                  {/* Add Products Button */}
                  <div className="p-3 border-t bg-gray-50">
                    <div className="flex justify-between font-bold mb-3">
                      <span>Total a agregar:</span>
                      <span>${total.toFixed(2)}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setShowAddToOrderPanel(false);
                          setSelectedOrder(null);
                          setCart([]);
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        className="flex-1 bg-orange-500 hover:bg-orange-600"
                        onClick={handleAddToExistingOrder}
                        disabled={processing || cart.length === 0}
                      >
                        Agregar
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Current Order Items */}
                  <div className="flex-1 overflow-auto p-3 min-h-0" style={{WebkitOverflowScrolling: 'touch'}}>
                    <div className="space-y-2 pb-4">
                      {currentOrder.items?.map((item, idx) => {
                        const isCancelled = item.item_status === 'cancelled';
                        return (
                          <div 
                            key={idx} 
                            className={`flex justify-between items-center text-sm py-2 border-b ${
                              isCancelled ? 'line-through text-gray-400 bg-red-50 rounded px-2' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
                                isCancelled ? 'bg-red-100 text-red-500' :
                                item.item_status === 'ready' ? 'bg-green-100 text-green-700' :
                                item.item_status === 'preparing' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {isCancelled ? <XCircle className="w-4 h-4" /> : item.quantity}
                              </span>
                              <div className="flex flex-col">
                                <div className="flex items-center gap-1">
                                  {item.is_promo_item && (
                                    <span className="text-xs px-1 py-0.5 rounded bg-purple-500 text-white font-bold">
                                      🏷️
                                    </span>
                                  )}
                                  <span>{item.product_name || item.name}</span>
                                </div>
                                {item.is_promo_item && item.promo_name && (
                                  <span className="text-xs text-purple-600">{item.promo_name}</span>
                                )}
                                {item.description && (
                                  <span className="text-xs text-amber-600">📋 {item.description}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">${item.subtotal?.toFixed(2)}</span>
                              {!isCancelled && currentOrder.status !== 'completed' && currentOrder.status !== 'cancelled' && (
                                <button
                                  onClick={() => openCancelExistingItemModal(currentOrder.order_id, idx, item.product_name)}
                                  className="w-6 h-6 bg-red-100 text-red-600 rounded flex items-center justify-center hover:bg-red-200"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Order Total & Actions */}
                  <div className="p-4 border-t bg-gray-50 pb-20 md:pb-4">
                    {currentOrder.discount_amount > 0 && (
                      <div className="space-y-1 mb-2 text-sm">
                        <div className="flex justify-between text-gray-600">
                          <span>Subtotal:</span>
                          <span>${currentOrder.subtotal?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-green-600" data-testid="takeout-discount-line">
                          <span>Descuento:</span>
                          <span>-${currentOrder.discount_amount?.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-xl mb-3">
                      <span>Total</span>
                      <span>${currentOrder.total?.toFixed(2)}</span>
                    </div>

                    {/* Status Actions */}
                    {currentOrder.status === 'pending' && (
                      <Button className="w-full bg-blue-500 hover:bg-blue-600 mb-2" onClick={() => updateOrderStatus(currentOrder.order_id, 'preparing')} disabled={updatingStatus}>
                        <ChefHat className="w-4 h-4 mr-2" /> {updatingStatus ? 'Procesando...' : 'Enviar a Preparar'}
                      </Button>
                    )}
                    {currentOrder.status === 'preparing' && (
                      <Button className="w-full bg-green-500 hover:bg-green-600 mb-2" onClick={() => updateOrderStatus(currentOrder.order_id, 'ready')} disabled={updatingStatus}>
                        <Check className="w-4 h-4 mr-2" /> {updatingStatus ? 'Procesando...' : 'Marcar como Listo'}
                      </Button>
                    )}
                    {currentOrder.status === 'ready' && (
                      <Button className="w-full bg-green-600 hover:bg-green-700 mb-2" onClick={() => openCloseOrderModal(currentOrder)} disabled={updatingStatus}>
                        <DollarSign className="w-4 h-4 mr-2" /> Entregar y Cobrar
                      </Button>
                    )}

                    {/* Secondary Actions */}
                    {currentOrder.status !== 'completed' && currentOrder.status !== 'cancelled' && (
                      <div className="flex gap-2 mt-2">
                        {canApplyDiscounts && (
                          <button
                            onClick={() => openDiscountModal(currentOrder)}
                            className="flex-1 text-xs text-orange-600 hover:bg-orange-50 py-2 rounded border border-orange-200 flex items-center justify-center gap-1"
                          >
                            <Percent className="w-3 h-3" /> Descuento
                          </button>
                        )}
                        {canVoidOrders && (
                          <button
                            onClick={() => openVoidOrderModal(currentOrder.order_id, currentOrder.order_number)}
                            className="flex-1 text-xs text-red-600 hover:bg-red-50 py-2 rounded border border-red-200 flex items-center justify-center gap-1"
                          >
                            <XCircle className="w-3 h-3" /> Cancelar
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : showNewOrderPanel && orderType === 'takeout' ? (
            <>
              {/* New Order Header */}
              <div className="bg-orange-500 text-white p-4 flex items-center justify-between">
                <h3 className="font-bold">NUEVO PEDIDO - MOSTRADOR</h3>
                <Button variant="ghost" size="icon" className="text-white hover:bg-orange-600" onClick={() => setShowNewOrderPanel(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="flex-1 overflow-auto">
                {/* Customer Info - Enhanced with CRM fields */}
                <div className="p-3 md:p-4 space-y-2 md:space-y-3 border-b">
                  <div className="grid grid-cols-2 gap-2 md:block md:space-y-3">
                    <div className="relative">
                      <Label className="text-xs text-gray-500">Cliente</Label>
                      <div className="flex items-center gap-1">
                        <Input 
                          value={customerName} 
                          onChange={handleNameChange} 
                          placeholder="Nombre"
                          className="flex-1 h-9 text-sm"
                          onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                          data-testid="mostrador-customer-name"
                        />
                        {isReturningCustomer && (
                          <span className="hidden md:inline px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full whitespace-nowrap">
                            Frecuente
                          </span>
                        )}
                      </div>
                      {showCustomerSuggestions && customerSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-48 overflow-auto">
                          {customerSuggestions.map(customer => (
                            <button
                              key={customer.customer_id}
                              onClick={() => selectCustomer(customer)}
                              className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b last:border-b-0"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-sm">{customer.name}</p>
                                  <p className="text-xs text-gray-500">{customer.phone}</p>
                                </div>
                                <div className="text-right">
                                  {customer.loyalty?.points_available > 0 ? (
                                    <>
                                      <p className="text-xs text-amber-600 font-bold">⭐ {customer.loyalty.points_available} pts</p>
                                      <p className="text-xs text-amber-500">${customer.loyalty.points_value_pesos}</p>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-xs text-green-600 font-medium">${customer.total_spent?.toFixed(0) || 0}</p>
                                      <p className="text-xs text-gray-400">{customer.visit_count || 0} visitas</p>
                                    </>
                                  )}
                                </div>
                              </div>
                              {customer.quick_stats?.favorites?.length > 0 && (
                                <p className="text-xs text-orange-500 mt-1 truncate">
                                  Favoritos: {customer.quick_stats.favorites.map(f => f.name).join(', ')}
                                </p>
                              )}
                              {customer.loyalty?.can_redeem && (
                                <p className="text-xs text-green-600 mt-1 font-medium">
                                  🎁 Puede canjear {customer.loyalty.redeemable_products?.length} productos
                                </p>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-gray-500">Teléfono</Label>
                      <Input 
                        value={customerPhone} 
                        onChange={handlePhoneChange} 
                        placeholder="Teléfono"
                        className="h-9 text-sm"
                        data-testid="mostrador-customer-phone"
                      />
                    </div>
                  </div>

                  {/* Additional CRM fields for new customer */}
                  {!selectedCustomer && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Colonia</Label>
                        <Input 
                          value={customerColonia} 
                          onChange={(e) => setCustomerColonia(e.target.value)} 
                          placeholder="Colonia"
                          className="h-9 text-sm"
                          data-testid="mostrador-customer-colonia"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Cumpleaños</Label>
                        <Input 
                          type="date"
                          value={customerBirthday} 
                          onChange={(e) => setCustomerBirthday(e.target.value)} 
                          className="h-9 text-sm"
                          data-testid="mostrador-customer-birthday"
                        />
                      </div>
                    </div>
                  )}

                  {/* Selected Customer Info Card - Enhanced */}
                  {selectedCustomer && (
                    <div className="p-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {selectedCustomer.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{selectedCustomer.name}</p>
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {selectedCustomer.phone}
                            </p>
                          </div>
                        </div>
                        <button onClick={clearSelectedCustomer} className="text-gray-400 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2 text-xs">
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                          Total: ${selectedCustomer.total_spent?.toFixed(0) || 0}
                        </span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          {selectedCustomer.visit_count || 0} visitas
                        </span>
                        {selectedCustomer.colonia && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {selectedCustomer.colonia}
                          </span>
                        )}
                        {selectedCustomer.birthday && (
                          <span className="px-2 py-0.5 bg-pink-100 text-pink-700 rounded-full">
                            🎂 {selectedCustomer.birthday}
                          </span>
                        )}
                      </div>
                      {selectedCustomer.last_visit && (
                        <p className="text-xs text-gray-500 mt-1">
                          Última visita: {new Date(selectedCustomer.last_visit).toLocaleDateString('es-MX')}
                        </p>
                      )}
                      {selectedCustomer.quick_stats?.favorites?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-orange-200">
                          <p className="text-xs text-gray-600 font-medium">Favoritos:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {selectedCustomer.quick_stats.favorites.slice(0, 3).map((fav, idx) => (
                              <span key={idx} className="text-xs bg-white px-2 py-0.5 rounded border">
                                {fav.name} ({fav.count})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Loyalty Points Banner */}
                      {selectedCustomer.loyalty?.points_available > 0 && (
                        <div className="mt-2 pt-2 border-t border-amber-300">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                                <Star className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-amber-700">
                                  {selectedCustomer.loyalty.points_available} puntos
                                </p>
                                <p className="text-xs text-amber-600">
                                  = ${selectedCustomer.loyalty.points_value_pesos} pesos
                                </p>
                              </div>
                            </div>
                            {selectedCustomer.loyalty.can_redeem && (
                              <Button
                                size="sm"
                                onClick={async () => {
                                  // Cargar productos canjeables si no están cargados
                                  if (vipRedeemableProducts.length === 0 && selectedCustomer?.phone) {
                                    try {
                                      const res = await api.get(`/customer-portal/redeemable-products/${user.user_id}?phone=${selectedCustomer.phone}`);
                                      const products = res.data.products || [];
                                      setVipRedeemableProducts(products.filter(p => p.can_afford));
                                    } catch (error) {
                                      console.error('Error loading redeemable products:', error);
                                    }
                                  }
                                  setShowLoyaltyPanel(true);
                                }}
                                className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-7"
                              >
                                <Gift className="w-3 h-3 mr-1" />
                                Canjear
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="hidden md:block">
                    <Label className="text-xs text-gray-500">Notas</Label>
                    <textarea
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      placeholder="Notas del pedido"
                      className="w-full border rounded p-2 text-sm h-16 resize-none"
                    />
                  </div>
                </div>

                {/* Category Tabs */}
                <div className="p-2 border-b overflow-x-auto bg-gray-50">
                  <div className="flex gap-1">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                        selectedCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-100 border'
                      }`}
                    >
                      Todos
                    </button>
                    {flatCategories.map(cat => (
                      <button
                        key={cat.category_id}
                        onClick={() => setSelectedCategory(cat.category_id)}
                        className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                          selectedCategory === cat.category_id ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-100 border'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Product Search - More space on mobile */}
                <div className="p-2 md:p-3 border-b">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Buscar producto..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-9 text-sm"
                        data-testid="new-takeout-product-search"
                      />
                    </div>
                    <button
                      onClick={openCustomProductModal}
                      className="text-xs bg-purple-500 text-white px-2 md:px-3 py-2 rounded hover:bg-purple-600 flex items-center gap-1 whitespace-nowrap"
                    >
                      <Star className="w-3 h-3" /> <span className="hidden sm:inline">Especial</span>
                    </button>
                  </div>
                  
                  <div className="h-[20vh] md:max-h-40 overflow-auto border rounded bg-white">
                    {filteredProducts.slice(0, 30).map(product => (
                      <button
                        key={product.product_id}
                        onClick={() => addToCart(product)}
                        className="w-full text-left p-2 hover:bg-orange-50 text-sm border-b last:border-b-0 active:bg-orange-100"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <span className="font-medium truncate block">{product.name}</span>
                            {product.description && (
                              <span className="text-xs text-gray-500 truncate block" dangerouslySetInnerHTML={{ __html: '→ ' + parseMarkdown(product.description) }} />
                            )}
                          </div>
                          <span className="text-orange-600 font-bold ml-2 whitespace-nowrap">${product.price?.toFixed(2)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cart - Always visible */}
                <div className="p-3 bg-orange-50 border-t">
                  {cart.length > 0 ? (
                    <div>
                      <Label className="text-xs text-gray-500 mb-2 block font-bold">🛒 PRODUCTOS ({cart.length})</Label>
                      <div className="space-y-2 max-h-32 overflow-auto">
                        {cart.map((item, cartIdx) => (
                          <div key={`${item.product_id}-${cartIdx}`} className="flex items-center justify-between py-2 px-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <button onClick={() => updateQuantity(item.product_id, -1)} className="w-6 h-6 bg-gray-200 rounded text-sm font-bold">-</button>
                              <span className="font-bold w-6 text-center">{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.product_id, 1)} className="w-6 h-6 bg-gray-200 rounded text-sm font-bold">+</button>
                              <div>
                                <span className="text-sm truncate max-w-[100px]">{item.name}</span>
                                {item.item_notes && <p className="text-xs text-orange-600">📝 {item.item_notes}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                              <button onClick={() => removeFromCart(item.product_id)} className="text-red-500">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-400">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Agrega productos al pedido</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer with Discount and Total */}
              <div className="p-4 border-t bg-gray-50 pb-20 md:pb-4">
                {/* Discount Section */}
                {cart.length > 0 && (
                  <div className="mb-3 pb-3 border-b">
                    {!showDiscountInput ? (
                      <button
                        onClick={() => setShowDiscountInput(true)}
                        className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700"
                        data-testid="add-discount-btn-takeout"
                      >
                        <Percent className="w-4 h-4" />
                        Agregar descuento
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            value={discountAmount || ''}
                            onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            className="flex-1 h-8"
                            autoFocus
                          />
                          <button
                            onClick={() => {
                              setShowDiscountInput(false);
                              setDiscountAmount(0);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {discountAmount > 0 && (
                          <p className="text-xs text-green-600">
                            Descuento: -${discountAmount.toFixed(2)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Totals */}
                {discountAmount > 0 && (
                  <div className="space-y-1 mb-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal:</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Descuento:</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg mb-3">
                  <span>Total:</span>
                  <span>${(subtotal - discountAmount).toFixed(2)}</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    className="flex-1 bg-orange-500 hover:bg-orange-600" 
                    onClick={handleCreateOrder}
                    disabled={processing || cart.length === 0}
                  >
                    Crear Pedido
                  </Button>
                  <Button 
                    className="flex-1 bg-blue-600 hover:bg-blue-700" 
                    onClick={handleCreateAndMarkReady}
                    disabled={processing || cart.length === 0}
                  >
                    <ChefHat className="w-4 h-4 mr-1" />
                    A Cocina
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-4">
              <Package className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">Selecciona un pedido</p>
              <p className="text-sm">o crea uno nuevo</p>
            </div>
          )}
        </div>
        </div>
      </div>
    );
  };

  // ==================== DOMICILIO (DELIVERY) VIEW ====================
  const renderDomicilioView = () => {
    const currentOrder = selectedDeliveryOrder;
    const allDeliveryOrders = [...deliveryPending, ...deliveryPreparing, ...deliveryReady, ...deliverySent];
    
    // Calculate stats for today
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayDeliveryCompleted = deliveryCompleted.filter(o => new Date(o.created_at) >= todayStart);
    const todayDeliverySales = todayDeliveryCompleted.reduce((sum, o) => sum + (o.total || 0), 0);
    
    return (
      <div className="flex flex-col h-full">
        {/* Stats Header Bar */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              <span className="font-bold text-lg">DOMICILIO</span>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-lg">
                <DollarSign className="w-4 h-4" />
                <span className="font-semibold">${todayDeliverySales.toFixed(0)}</span>
                <span className="text-xs opacity-80">hoy</span>
              </div>
              <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-lg">
                <Clock className="w-4 h-4" />
                <span className="font-semibold">{allDeliveryOrders.length}</span>
                <span className="text-xs opacity-80">activas</span>
              </div>
              <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-lg">
                <CheckCircle className="w-4 h-4" />
                <span className="font-semibold">{todayDeliveryCompleted.length}</span>
                <span className="text-xs opacity-80">entregadas</span>
              </div>
              <div className="flex items-center gap-1 bg-yellow-400/30 px-3 py-1 rounded-lg">
                <Truck className="w-4 h-4" />
                <span className="font-semibold">{deliverySent.length}</span>
                <span className="text-xs opacity-80">en ruta</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => cleanupGhostOrders('delivery')}
              className="px-3 py-2 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-lg flex items-center gap-1"
              title="Limpiar pedidos fantasma de domicilio"
              data-testid="cleanup-ghost-delivery-btn"
            >
              <Trash2 size={14} /> Limpiar Fantasmas
            </button>
            <Button 
              className="bg-white text-purple-600 hover:bg-purple-50 font-bold shadow-lg"
              onClick={() => {
                clearForm();
                setOrderType('delivery');
                setSelectedDeliveryOrder(null);
                setShowNewOrderPanel(true);
              }}
              data-testid="new-delivery-order-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Nuevo Pedido
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Order Cards */}
          <div className={`flex-1 p-4 overflow-auto bg-gray-50 ${currentOrder ? 'hidden md:block' : ''}`}>
            {/* Search */}
            <div className="mb-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Buscar por cliente, dirección o #orden..." 
                  className="pl-9 bg-white"
                  value={orderSearchQuery}
                  onChange={(e) => setOrderSearchQuery(e.target.value)}
                  data-testid="delivery-order-search"
                />
              </div>
            </div>

            {/* Order Cards by Status */}
            {allDeliveryOrders.length > 0 ? (
              <div className="space-y-6">
                {/* Pending */}
                {deliveryPending.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse"></div>
                      <span className="text-sm font-bold text-gray-600">PENDIENTES ({deliveryPending.length})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {deliveryPending.filter(o => !orderSearchQuery || o.order_number?.toString().includes(orderSearchQuery.toLowerCase()) || o.customer_name?.toLowerCase().includes(orderSearchQuery.toLowerCase()) || o.customer_address?.toLowerCase().includes(orderSearchQuery.toLowerCase())).map(order => (
                        <button key={order.order_id} onClick={() => { setSelectedDeliveryOrder(order); setShowNewOrderPanel(false); }}
                          className={`p-4 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 text-white text-left shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all ${currentOrder?.order_id === order.order_id ? 'ring-4 ring-amber-300 ring-offset-2' : ''} ${order.split_from ? 'ring-2 ring-purple-400' : ''}`}
                          data-testid={`delivery-order-${order.order_id}`}>
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-1">
                              <span className="text-2xl font-black">#{order.order_number}</span>
                              {order.split_from && <span className="px-1.5 py-0.5 bg-purple-600 rounded text-[8px] font-bold animate-pulse">DIVIDIDA</span>}
                            </div>
                            <span className="px-2 py-0.5 bg-white/25 rounded-full text-[10px] font-bold">{getTimeElapsed(order.created_at)}</span>
                          </div>
                          <p className="font-semibold mt-2 truncate">{order.customer_name || 'Sin nombre'}</p>
                          {order.customer_address && (
                            <p className="text-xs opacity-90 mt-1 truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{order.customer_address}</p>
                          )}
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xl font-black">${order.total?.toFixed(2)}</span>
                            <span className="text-xs opacity-75">{order.items?.length || 0} items</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preparing */}
                {deliveryPreparing.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse"></div>
                      <span className="text-sm font-bold text-gray-600">EN PREPARACIÓN ({deliveryPreparing.length})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {deliveryPreparing.filter(o => !orderSearchQuery || o.order_number?.toString().includes(orderSearchQuery.toLowerCase()) || o.customer_name?.toLowerCase().includes(orderSearchQuery.toLowerCase()) || o.customer_address?.toLowerCase().includes(orderSearchQuery.toLowerCase())).map(order => (
                        <button key={order.order_id} onClick={() => { setSelectedDeliveryOrder(order); setShowNewOrderPanel(false); }}
                          className={`p-4 rounded-xl bg-gradient-to-br from-blue-400 to-blue-500 text-white text-left shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all ${currentOrder?.order_id === order.order_id ? 'ring-4 ring-blue-300 ring-offset-2' : ''}`}
                          data-testid={`delivery-order-${order.order_id}`}>
                          <div className="flex justify-between items-start">
                            <span className="text-2xl font-black">#{order.order_number}</span>
                            <span className="px-2 py-0.5 bg-white/25 rounded-full text-[10px] font-bold flex items-center gap-1"><ChefHat className="w-3 h-3" />{getTimeElapsed(order.created_at)}</span>
                          </div>
                          <p className="font-semibold mt-2 truncate">{order.customer_name || 'Sin nombre'}</p>
                          {order.customer_address && (
                            <p className="text-xs opacity-90 mt-1 truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{order.customer_address}</p>
                          )}
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xl font-black">${order.total?.toFixed(2)}</span>
                            <span className="text-xs opacity-75">{order.items?.length || 0} items</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ready for dispatch */}
                {deliveryReady.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-sm font-bold text-gray-600">LISTAS PARA ENVIAR ({deliveryReady.length})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {deliveryReady.filter(o => !orderSearchQuery || o.order_number?.toString().includes(orderSearchQuery.toLowerCase()) || o.customer_name?.toLowerCase().includes(orderSearchQuery.toLowerCase()) || o.customer_address?.toLowerCase().includes(orderSearchQuery.toLowerCase())).map(order => (
                        <button key={order.order_id} onClick={() => { setSelectedDeliveryOrder(order); setShowNewOrderPanel(false); }}
                          className={`p-4 rounded-xl bg-gradient-to-br from-green-400 to-green-500 text-white text-left shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all ${currentOrder?.order_id === order.order_id ? 'ring-4 ring-green-300 ring-offset-2' : ''}`}
                          data-testid={`delivery-order-${order.order_id}`}>
                          <div className="flex justify-between items-start">
                            <span className="text-2xl font-black">#{order.order_number}</span>
                            <span className="px-2 py-0.5 bg-white/25 rounded-full text-[10px] font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" />LISTO</span>
                          </div>
                          <p className="font-semibold mt-2 truncate">{order.customer_name || 'Sin nombre'}</p>
                          {order.customer_address && (
                            <p className="text-xs opacity-90 mt-1 truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{order.customer_address}</p>
                          )}
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xl font-black">${order.total?.toFixed(2)}</span>
                            <span className="px-2 py-0.5 bg-white/30 rounded text-xs font-bold">DESPACHAR</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* In Transit / Sent */}
                {deliverySent.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-purple-500 animate-pulse"></div>
                      <span className="text-sm font-bold text-gray-600">EN CAMINO ({deliverySent.length})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {deliverySent.filter(o => !orderSearchQuery || o.order_number?.toString().includes(orderSearchQuery.toLowerCase()) || o.customer_name?.toLowerCase().includes(orderSearchQuery.toLowerCase()) || o.customer_address?.toLowerCase().includes(orderSearchQuery.toLowerCase())).map(order => (
                        <button key={order.order_id} onClick={() => { setSelectedDeliveryOrder(order); setShowNewOrderPanel(false); }}
                          className={`p-4 rounded-xl bg-gradient-to-br from-purple-400 to-purple-500 text-white text-left shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all ${currentOrder?.order_id === order.order_id ? 'ring-4 ring-purple-300 ring-offset-2' : ''}`}
                          data-testid={`delivery-order-${order.order_id}`}>
                          <div className="flex justify-between items-start">
                            <span className="text-2xl font-black">#{order.order_number}</span>
                            <span className="px-2 py-0.5 bg-white/25 rounded-full text-[10px] font-bold flex items-center gap-1"><Truck className="w-3 h-3" />EN RUTA</span>
                          </div>
                          <p className="font-semibold mt-2 truncate">{order.customer_name || 'Sin nombre'}</p>
                          {order.customer_address && (
                            <p className="text-xs opacity-90 mt-1 truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{order.customer_address}</p>
                          )}
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xl font-black">${order.total?.toFixed(2)}</span>
                            <span className="px-2 py-0.5 bg-yellow-400/50 rounded text-xs font-bold">COBRAR</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : !showNewOrderPanel && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Truck className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg font-medium text-gray-500">No hay pedidos activos</p>
                <p className="text-sm">Haz clic en "Nuevo Pedido" para crear uno</p>
              </div>
            )}

            {/* Completed Orders - Compact History */}
            {todayDeliveryCompleted.length > 0 && (
              <div className="mt-8 pt-6 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-600 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    ENTREGAS DE HOY ({todayDeliveryCompleted.length})
                  </h3>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                  {todayDeliveryCompleted.slice(0, 16).map(order => (
                    <div key={order.order_id} className="p-2 rounded-lg bg-white border hover:border-gray-300 transition-colors cursor-pointer" onClick={() => setSelectedDeliveryOrder(order)}>
                      <div className="font-bold text-gray-800">#{order.order_number}</div>
                      <div className="text-xs text-gray-500 truncate">{order.customer_name || '-'}</div>
                      <div className="text-sm font-semibold text-green-600">${order.total?.toFixed(2)}</div>
                      <div className="text-[10px] text-gray-400">{new Date(order.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Hermosillo' })}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>

        {/* Right Panel */}
        <div className={`${currentOrder || showNewOrderPanel || showAddToOrderPanel ? 'fixed inset-0 z-40 pt-14 md:pt-0 md:relative md:inset-auto' : 'hidden md:block'} w-full md:w-96 border-l bg-white flex flex-col`} style={{maxHeight: (currentOrder || showNewOrderPanel || showAddToOrderPanel) ? 'calc(100vh - 56px)' : 'auto'}}>
          {/* Add to Existing Order Panel for Delivery */}
          {showAddToOrderPanel && selectedOrder ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="p-3 bg-gray-800 text-white flex justify-between items-center">
                <span className="font-bold text-sm">ADICIONAR A PEDIDO #{selectedOrder.order_number}</span>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => { setShowAddToOrderPanel(false); setSelectedOrder(null); setCart([]); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Category Tabs */}
              <div className="p-2 border-b overflow-x-auto">
                <div className="flex gap-1">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                      selectedCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    Todos
                  </button>
                  {flatCategories.map(cat => (
                    <button
                      key={cat.category_id}
                      onClick={() => setSelectedCategory(cat.category_id)}
                      className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${
                        selectedCategory === cat.category_id ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Product Search */}
              <div className="p-3 border-b">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-orange-500 rounded flex items-center justify-center">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <div className="relative flex-1">
                    <Input
                      placeholder="Buscar producto"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-gray-100"
                      data-testid="delivery-add-product-search"
                    />
                  </div>
                  <button
                    onClick={openCustomProductModal}
                    className="text-xs bg-purple-500 text-white px-3 py-2 rounded hover:bg-purple-600 flex items-center gap-1 whitespace-nowrap"
                  >
                    <Star className="w-3 h-3" /> Especial
                  </button>
                </div>
                
                {/* Search Results */}
                {searchQuery.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-auto border rounded">
                    {filteredProducts.slice(0, 10).map(product => (
                      <button
                        key={product.product_id}
                        onClick={() => addToCart(product)}
                        className="w-full text-left p-2 hover:bg-orange-50 text-sm flex justify-between border-b"
                      >
                        <span>{product.name}</span>
                        <span className="text-gray-500">${product.price?.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Cart for Adding */}
              <div className="flex-1 overflow-auto p-3 min-h-0" style={{WebkitOverflowScrolling: 'touch'}}>
                {cart.length > 0 ? (
                  <div className="space-y-2">
                    {cart.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-orange-50 p-2 rounded">
                        <div className="flex items-center gap-2 flex-1">
                          <div className="flex items-center gap-1">
                            <button onClick={() => updateQuantity(item.product_id, -1)} className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center hover:bg-gray-300">-</button>
                            <span className="w-6 text-center">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product_id, 1)} className="w-6 h-6 bg-orange-500 text-white rounded flex items-center justify-center hover:bg-orange-600">+</button>
                          </div>
                          <span className="truncate">{item.name}</span>
                        </div>
                        <span className="font-medium ml-2">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <ShoppingCart className="w-12 h-12 mb-2" />
                    <p className="text-sm">Busca productos para agregar</p>
                  </div>
                )}
              </div>

              {/* Footer - Add Button */}
              <div className="p-4 border-t bg-gray-50 pb-20 md:pb-4">
                <div className="flex justify-between font-bold text-lg mb-3">
                  <span>Total a agregar:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700" 
                  onClick={handleAddToExistingOrder}
                  disabled={processing || cart.length === 0}
                >
                  {processing ? 'Agregando...' : `Agregar ${cart.length} producto(s)`}
                </Button>
              </div>
            </div>
          ) : currentOrder ? (
            <>
              <div className={`${ORDER_STATUSES[currentOrder.status]?.color || 'bg-orange-500'} text-white p-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">PEDIDO #{currentOrder.order_number}</h2>
                    <p className="text-sm opacity-90">{currentOrder.customer_name || 'Sin nombre'}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => setSelectedDeliveryOrder(null)}>
                    <X className="w-6 h-6" />
                  </Button>
                </div>
                <Button size="sm" className="mt-3 bg-white text-orange-600 hover:bg-gray-100" onClick={() => selectOrderForEditing(currentOrder)}>
                  + Añadir productos
                </Button>
              </div>

              <div className="px-4 py-2 text-xs text-gray-600 border-b bg-gray-50 space-y-1">
                <div className="flex items-center gap-2"><Clock className="w-3 h-3" />{formatDateTime(currentOrder.created_at)}</div>
                {currentOrder.customer_phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{currentOrder.customer_phone}</div>}
                {currentOrder.customer_address && <div className="flex items-center gap-2"><MapPin className="w-3 h-3" />{currentOrder.customer_address}</div>}
              </div>

              <div className="flex-1 overflow-auto p-3 min-h-0 pb-20" style={{WebkitOverflowScrolling: 'touch'}}>
                {currentOrder.items?.map((item, idx) => {
                  const isCancelled = item.item_status === 'cancelled';
                  return (
                    <div key={idx} className={`flex justify-between items-center text-sm py-2 border-b ${isCancelled ? 'line-through text-gray-400 bg-red-50' : ''}`}>
                      <div className="flex items-center gap-2 flex-1">
                        <span className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-xs">{item.quantity}</span>
                        <div className="flex-1">
                          <span>{item.product_name || item.name}</span>
                          {isCancelled && item.cancel_reason && (
                            <p className="text-xs text-red-500">Motivo: {item.cancel_reason}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">${item.subtotal?.toFixed(2)}</span>
                        {!isCancelled && currentOrder.status !== 'completed' && currentOrder.status !== 'cancelled' && (
                          <button
                            onClick={() => openCancelExistingItemModal(currentOrder.order_id, idx, item.product_name || item.name)}
                            className="w-6 h-6 bg-red-100 text-red-600 rounded flex items-center justify-center hover:bg-red-200 transition-colors"
                            title="Cancelar item"
                            data-testid={`cancel-delivery-item-${currentOrder.order_id}-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-4 border-t bg-gray-50 pb-20 md:pb-4">
                {/* Show discount if applied */}
                {currentOrder.discount_amount > 0 && (
                  <div className="space-y-1 mb-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal:</span>
                      <span>${currentOrder.subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Descuento:</span>
                      <span>-${currentOrder.discount_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <div className="flex justify-between font-bold text-xl mb-3">
                  <span>Total</span>
                  <span>${currentOrder.total?.toFixed(2)}</span>
                </div>
                {currentOrder.status === 'pending' && <Button className="w-full bg-blue-500 hover:bg-blue-600 mb-2" onClick={() => updateOrderStatus(currentOrder.order_id, 'preparing')}><ChefHat className="w-4 h-4 mr-2" />Preparar</Button>}
                {currentOrder.status === 'preparing' && <Button className="w-full bg-green-500 hover:bg-green-600 mb-2" onClick={() => updateOrderStatus(currentOrder.order_id, 'ready')}><Check className="w-4 h-4 mr-2" />Listo</Button>}
                {currentOrder.status === 'ready' && <Button className="w-full bg-purple-500 hover:bg-purple-600 mb-2" onClick={() => updateOrderStatus(currentOrder.order_id, 'sent')}><Truck className="w-4 h-4 mr-2" />En Camino</Button>}
                {currentOrder.status === 'sent' && <Button className="w-full bg-green-600 hover:bg-green-700 mb-2" onClick={() => openCloseOrderModal(currentOrder)}><DollarSign className="w-4 h-4 mr-2" />Cobrar Entrega</Button>}
                {(canApplyDiscounts || canVoidOrders) && (
                  <div className="flex gap-2 mt-2">
                    {canApplyDiscounts && (
                      <button onClick={() => openDiscountModal(currentOrder)} className="flex-1 text-xs text-orange-600 hover:bg-orange-50 py-2 rounded border border-orange-200 flex items-center justify-center gap-1"><Percent className="w-3 h-3" />Descuento</button>
                    )}
                    {canVoidOrders && (
                      <button onClick={() => openVoidOrderModal(currentOrder.order_id, currentOrder.order_number)} className="flex-1 text-xs text-red-600 hover:bg-red-50 py-2 rounded border border-red-200 flex items-center justify-center gap-1"><XCircle className="w-3 h-3" />Cancelar</button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : showNewOrderPanel && orderType === 'delivery' ? (
            <>
              <div className="bg-orange-500 text-white p-3 md:p-4 flex items-center justify-between">
                <h3 className="font-bold text-sm md:text-base">NUEVO - DOMICILIO</h3>
                <Button variant="ghost" size="icon" className="text-white hover:bg-orange-600" onClick={() => setShowNewOrderPanel(false)}><X className="w-5 h-5" /></Button>
              </div>

              <div className="flex-1 overflow-auto">
                {/* Customer Info - Compact grid on mobile */}
                <div className="p-2 md:p-4 space-y-2 md:space-y-3 border-b">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-500">Teléfono *</Label>
                      <Input 
                        value={customerPhone} 
                        onChange={handlePhoneChange} 
                        placeholder="Teléfono" 
                        className="h-9 text-sm"
                        onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                      />
                    </div>
                    <div className="relative">
                      <Label className="text-xs text-gray-500">Cliente</Label>
                      <Input 
                        value={customerName} 
                        onChange={handleNameChange} 
                        placeholder="Nombre" 
                        className="h-9 text-sm"
                        onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                      />
                      {/* Customer Suggestions Dropdown */}
                      {showCustomerSuggestions && customerSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                          {customerSuggestions.map(customer => (
                            <div
                              key={customer.customer_id}
                              onClick={() => selectCustomer(customer)}
                              className="p-2 hover:bg-orange-50 cursor-pointer border-b last:border-b-0"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-sm">{customer.name}</p>
                                  <p className="text-xs text-gray-500">{customer.phone}</p>
                                </div>
                                {customer.loyalty?.points_available > 0 && (
                                  <div className="flex items-center gap-1 bg-amber-100 px-2 py-1 rounded-full">
                                    <Star className="w-3 h-3 text-amber-500" />
                                    <span className="text-xs font-medium text-amber-700">
                                      {customer.loyalty.points_available} pts
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Dirección *</Label>
                    <Input value={customerStreet} onChange={(e) => setCustomerStreet(e.target.value)} placeholder="Calle y número" className="mb-1 h-9 text-sm" />
                    <div className="grid grid-cols-3 gap-1">
                      <Input value={customerNumber} onChange={(e) => setCustomerNumber(e.target.value)} placeholder="Num" className="h-8 text-sm" />
                      <Input value={customerFloor} onChange={(e) => setCustomerFloor(e.target.value)} placeholder="Piso" className="h-8 text-sm" />
                      <Input value={customerNeighborhood} onChange={(e) => setCustomerNeighborhood(e.target.value)} placeholder="Colonia" className="h-8 text-sm" />
                    </div>
                  </div>
                </div>

                <div className="p-2 border-b overflow-x-auto bg-gray-50">
                  <div className="flex gap-1">
                    <button onClick={() => setSelectedCategory('all')} className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${selectedCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-100 border'}`}>Todos</button>
                    {flatCategories.map(cat => (<button key={cat.category_id} onClick={() => setSelectedCategory(cat.category_id)} className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${selectedCategory === cat.category_id ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-100 border'}`}>{cat.name}</button>))}
                  </div>
                </div>

                {/* Product Search - More space on mobile */}
                <div className="p-2 md:p-3 border-b">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Buscar producto..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" /></div>
                    <button onClick={openCustomProductModal} className="text-xs bg-purple-500 text-white px-2 md:px-3 py-2 rounded hover:bg-purple-600 flex items-center gap-1"><Star className="w-3 h-3" /><span className="hidden sm:inline">Especial</span></button>
                  </div>
                  <div className="h-[30vh] md:max-h-40 overflow-auto border rounded bg-white">
                    {filteredProducts.slice(0, 30).map(product => (<button key={product.product_id} onClick={() => addToCart(product)} className="w-full text-left p-2 hover:bg-orange-50 text-sm flex justify-between border-b last:border-b-0 active:bg-orange-100"><span className="truncate">{product.name}</span><span className="text-gray-500 ml-2">${product.price?.toFixed(2)}</span></button>))}
                  </div>
                </div>

                <div className="p-2 md:p-3">
                  {cart.length > 0 ? (
                    <div>
                      <Label className="text-xs text-gray-500 mb-2 block">PRODUCTOS ({cart.length})</Label>
                      <div className="space-y-2 max-h-32 md:max-h-48 overflow-auto">
                        {cart.map((item, cartIdx) => (<div key={`${item.product_id}-${cartIdx}`} className="flex items-center justify-between py-2 px-2 bg-gray-50 rounded"><div className="flex items-center gap-2"><button onClick={() => updateQuantity(item.product_id, -1)} className="w-6 h-6 bg-gray-200 rounded text-sm font-bold">-</button><span className="font-bold w-6 text-center">{item.quantity}</span><button onClick={() => updateQuantity(item.product_id, 1)} className="w-6 h-6 bg-gray-200 rounded text-sm font-bold">+</button><span className="text-sm truncate max-w-[100px]">{item.name}</span></div><div className="flex items-center gap-2"><span className="text-sm font-medium">${(item.price * item.quantity).toFixed(2)}</span><button onClick={() => removeFromCart(item.product_id)} className="text-red-500"><X className="w-4 h-4" /></button></div></div>))}
                      </div>
                    </div>
                  ) : (<div className="text-center py-4 text-gray-400"><Truck className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">Agrega productos</p></div>)}
                </div>
              </div>

              <div className="p-4 border-t bg-gray-50 pb-20 md:pb-4">
                {/* Discount Section */}
                {cart.length > 0 && (
                  <div className="mb-3 pb-3 border-b">
                    {!showDiscountInput ? (
                      <button
                        onClick={() => setShowDiscountInput(true)}
                        className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700"
                        data-testid="add-discount-btn-delivery"
                      >
                        <Percent className="w-4 h-4" />
                        Agregar descuento
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            value={discountAmount || ''}
                            onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            className="flex-1 h-8"
                            autoFocus
                          />
                          <button
                            onClick={() => {
                              setShowDiscountInput(false);
                              setDiscountAmount(0);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {discountAmount > 0 && (
                          <p className="text-xs text-green-600">
                            Descuento: -${discountAmount.toFixed(2)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Totals */}
                {discountAmount > 0 && (
                  <div className="space-y-1 mb-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal:</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Descuento:</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg mb-3"><span>Total:</span><span>${(subtotal - discountAmount).toFixed(2)}</span></div>
                <Button className="w-full bg-orange-500 hover:bg-orange-600" onClick={handleCreateOrder} disabled={processing || cart.length === 0 || !customerStreet}>Crear Pedido</Button>
              </div>
            </>
          ) : (<div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-4"><Truck className="w-16 h-16 mb-4 opacity-30" /><p className="text-lg font-medium">Selecciona un pedido</p><p className="text-sm">o crea uno nuevo</p></div>)}
        </div>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================
  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center md:ml-64">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
        </main>
      </div>
    );
  }

  const tabs = [
    { id: 'dine_in', label: 'Mesas', icon: Utensils, count: tables.filter(t => getTableStatus(t) === 'occupied').length },
    { id: 'takeout', label: 'Mostrador', icon: Package, count: takeoutPending.length + takeoutPreparing.length + takeoutReady.length },
    { id: 'delivery', label: 'Domicilio', icon: Truck, count: deliveryPending.length + deliveryPreparing.length + deliveryReady.length + deliverySent.length }
  ];

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <main className="flex-1 flex flex-col md:ml-64 pt-14 md:pt-0">
        {/* Header with Tabs */}
        <div className="bg-white border-b px-3 md:px-4 py-2">
          <div className="flex items-center justify-between">
            <h1 className="text-lg md:text-xl font-bold text-gray-800">Terminal POS</h1>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    fetchPendingRedemptions();
                    setRedeemModalOpen(true);
                  }}
                  className="h-8 md:h-9 text-xs md:text-sm bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                >
                  <Gift className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Canjear Puntos</span>
                  <span className="sm:hidden">Canje</span>
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={fetchData} className="h-8 w-8 md:h-9 md:w-9">
                <RefreshCw className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
              <a 
                href="/kitchen-monitor" 
                target="_blank" 
                className="hidden sm:flex items-center gap-1 text-sm text-orange-600 hover:underline"
              >
                <ChefHat className="w-4 h-4" /> Cocina
              </a>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-2">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setOrderType(tab.id);
                    setShowNewOrderPanel(false);
                    setSelectedTable(null);
                  }}
                  className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 rounded-t text-xs md:text-sm font-medium transition-colors ${
                    orderType === tab.id 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                      orderType === tab.id ? 'bg-white text-orange-500' : 'bg-orange-500 text-white'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {orderType === 'dine_in' && renderMesasView()}
          {orderType === 'takeout' && renderMostradorView()}
          {orderType === 'delivery' && renderDomicilioView()}
        </div>
      </main>

      {/* Weight Input Modal for products sold by weight */}
      {/* Weight Modal */}
      <WeightModal
        isOpen={weightModalOpen}
        product={weightProduct}
        weightInput={weightInput}
        setWeightInput={setWeightInput}
        onConfirm={confirmWeightProduct}
        onClose={() => {
          setWeightModalOpen(false);
          setWeightProduct(null);
          setWeightInput('');
        }}
      />

      {/* Cancel Item Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="cancel-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-red-500 text-white px-4 py-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <h3 className="font-bold">Cancelar Producto</h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">
                Por favor, ingresa el motivo de la cancelación:
              </p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Motivo de cancelación..."
                className="w-full border rounded p-2 text-sm h-24 focus:ring-2 focus:ring-red-200 focus:border-red-400"
                autoFocus
                data-testid="cancel-reason-input"
              />
              <div className="flex justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCancelModalOpen(false);
                    setCancelItemId(null);
                    setCancelReason('');
                  }}
                  data-testid="cancel-modal-close"
                >
                  Cerrar
                </Button>
                <Button
                  className="bg-red-500 hover:bg-red-600 text-white"
                  onClick={confirmCancelItem}
                  data-testid="cancel-modal-confirm"
                >
                  Confirmar Cancelación
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Existing Order Item Modal */}
      <CancelExistingItemModal
        isOpen={cancelExistingItemModalOpen}
        itemName={cancelExistingItemName}
        cancelReason={cancelExistingReason}
        setCancelReason={setCancelExistingReason}
        onConfirm={confirmCancelExistingItem}
        onClose={() => {
          setCancelExistingItemModalOpen(false);
          setCancelExistingOrderId(null);
          setCancelExistingItemIndex(null);
          setCancelExistingItemName('');
          setCancelExistingReason('');
        }}
        processing={processing}
      />

      {/* Split Bill Modal */}
      {splitModalOpen && (() => {
        const order = allOrders.find(o => o.order_id === splitOrderId);
        if (!order) return null;
        const splitTotal = getSplitTotal(order);
        const hasSelectedItems = Object.values(splitItems).some(qty => qty > 0);
        const hasMultiAssignments = Object.values(multiSplitAssignments).some(bill => bill > 0);
        
        // Generate colors for each bill
        const billColors = [
          'bg-gray-100 border-gray-300 text-gray-700', // Bill 1 (original)
          'bg-blue-100 border-blue-300 text-blue-700',
          'bg-green-100 border-green-300 text-green-700',
          'bg-purple-100 border-purple-300 text-purple-700',
          'bg-orange-100 border-orange-300 text-orange-700',
          'bg-pink-100 border-pink-300 text-pink-700',
          'bg-teal-100 border-teal-300 text-teal-700',
          'bg-yellow-100 border-yellow-300 text-yellow-700',
          'bg-red-100 border-red-300 text-red-700',
          'bg-indigo-100 border-indigo-300 text-indigo-700',
        ];
        
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="split-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
              <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <h3 className="font-bold">Dividir Cuenta - Venta #{order.order_number}</h3>
                </div>
                <button onClick={() => setSplitModalOpen(false)} className="hover:bg-blue-700 p-1 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Mode Selection */}
              <div className="p-4 border-b bg-gray-50">
                <div className="flex gap-2">
                  <button
                    onClick={() => setSplitMode('single')}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                      splitMode === 'single' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    1 Cuenta Nueva
                  </button>
                  <button
                    onClick={() => setSplitMode('multiple')}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                      splitMode === 'multiple' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    Múltiples Cuentas
                  </button>
                </div>
              </div>
              
              <div className="p-4 flex-1 overflow-auto">
                {splitMode === 'single' ? (
                  <>
                    <p className="text-sm text-gray-600 mb-4">
                      Selecciona los productos que deseas mover a una nueva cuenta:
                    </p>
                    
                    {/* Customer name for new bill */}
                    <div className="mb-4">
                      <label className="text-xs text-gray-500 block mb-1">Nombre para la nueva cuenta (opcional)</label>
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-gray-400" />
                        <Input
                          value={splitCustomerName}
                          onChange={(e) => setSplitCustomerName(e.target.value)}
                          placeholder="Ej: Mesa 1 - Cliente 2"
                          className="flex-1"
                          data-testid="split-customer-name"
                        />
                      </div>
                    </div>

                    {/* Items list - Single mode */}
                    <div className="space-y-2 mb-4">
                      {order.items?.map((item, idx) => {
                        const selectedQty = splitItems[item.product_id] || 0;
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-center justify-between p-3 rounded border ${
                              selectedQty > 0 ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'
                            }`}
                            data-testid={`split-item-${item.product_id}`}
                          >
                            <div className="flex-1">
                              <p className="font-medium text-sm">{item.product_name}</p>
                              <p className="text-xs text-gray-500">
                                ${item.unit_price?.toFixed(2)} c/u • Disponible: {item.quantity}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateSplitQuantity(item.product_id, item.quantity, -1)}
                                className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center hover:bg-gray-300 disabled:opacity-50"
                                disabled={selectedQty === 0}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className={`w-8 text-center font-bold ${selectedQty > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                {selectedQty}
                              </span>
                              <button
                                onClick={() => updateSplitQuantity(item.product_id, item.quantity, 1)}
                                className="w-8 h-8 bg-blue-500 text-white rounded flex items-center justify-center hover:bg-blue-600 disabled:opacity-50"
                                disabled={selectedQty >= item.quantity}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Summary - Single mode */}
                    {hasSelectedItems && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-blue-800">Nueva cuenta:</span>
                          <span className="text-lg font-bold text-blue-600" data-testid="split-total">
                            ${splitTotal.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-blue-600 mt-1">
                          {Object.values(splitItems).reduce((a, b) => a + b, 0)} producto(s) seleccionado(s)
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Multiple Bills Mode */}
                    <div className="mb-4">
                      <label className="text-sm font-medium text-gray-700 block mb-2">
                        ¿En cuántas cuentas dividir?
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setNumSplitBills(Math.max(2, numSplitBills - 1))}
                          className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-300"
                          disabled={numSplitBills <= 2}
                        >
                          <Minus className="w-5 h-5" />
                        </button>
                        <span className="text-2xl font-bold text-blue-600 w-12 text-center">{numSplitBills}</span>
                        <button
                          onClick={() => setNumSplitBills(Math.min(10, numSplitBills + 1))}
                          className="w-10 h-10 bg-blue-500 text-white rounded-lg flex items-center justify-center hover:bg-blue-600"
                          disabled={numSplitBills >= 10}
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                        <span className="text-sm text-gray-500">cuentas (máx 10)</span>
                      </div>
                    </div>
                    
                    {/* Bill labels */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {Array.from({ length: numSplitBills }, (_, i) => i + 1).map(billNum => (
                        <div key={billNum} className={`px-3 py-1 rounded-full text-sm font-medium border ${billColors[billNum - 1] || billColors[0]}`}>
                          Cuenta {billNum} {billNum === 1 ? '(Original)' : ''}
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3">
                      Asigna cada producto a una cuenta (toca el número de cuenta):
                    </p>

                    {/* Items list - Multiple mode */}
                    <div className="space-y-2 mb-4">
                      {order.items?.map((item, itemIdx) => (
                        <div key={itemIdx} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-sm">{item.product_name}</p>
                            <p className="text-xs text-gray-500">${item.unit_price?.toFixed(2)} c/u</p>
                          </div>
                          {/* Individual units */}
                          <div className="flex flex-wrap gap-2">
                            {Array.from({ length: item.quantity }, (_, unitIdx) => {
                              const key = `${item.product_id}_${unitIdx}`;
                              const assignedBill = multiSplitAssignments[key] || 1;
                              return (
                                <div key={unitIdx} className="flex items-center gap-1">
                                  <span className="text-xs text-gray-400">#{unitIdx + 1}:</span>
                                  <select
                                    value={assignedBill}
                                    onChange={(e) => assignToBill(item.product_id, unitIdx, parseInt(e.target.value))}
                                    className={`text-sm font-medium px-2 py-1 rounded border ${billColors[assignedBill - 1] || billColors[0]}`}
                                  >
                                    {Array.from({ length: numSplitBills }, (_, i) => i + 1).map(billNum => (
                                      <option key={billNum} value={billNum}>
                                        Cuenta {billNum}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Summary - Multiple mode */}
                    <div className="space-y-2">
                      {Array.from({ length: numSplitBills }, (_, i) => i + 1).map(billNum => {
                        const billTotal = getBillTotal(order, billNum);
                        const billItems = getItemsForBill(order, billNum);
                        // For bill 1, also count unassigned items
                        let displayTotal = billTotal;
                        let displayItems = billItems.length;
                        if (billNum === 1) {
                          order.items?.forEach(item => {
                            for (let i = 0; i < item.quantity; i++) {
                              const key = `${item.product_id}_${i}`;
                              if (!multiSplitAssignments[key] || multiSplitAssignments[key] === 1) {
                                if (!billItems.find(x => x.product_id === item.product_id)) {
                                  displayTotal += item.unit_price;
                                  displayItems++;
                                }
                              }
                            }
                          });
                        }
                        return (
                          <div key={billNum} className={`flex justify-between items-center p-2 rounded border ${billColors[billNum - 1] || billColors[0]}`}>
                            <span className="text-sm font-medium">
                              Cuenta {billNum} {billNum === 1 ? '(Original)' : '(Nueva)'}
                            </span>
                            <span className="font-bold">${getBillTotal(order, billNum).toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSplitModalOpen(false);
                    setSplitOrderId(null);
                    setSplitItems({});
                    setSplitCustomerName('');
                    setMultiSplitAssignments({});
                  }}
                  data-testid="split-modal-close"
                >
                  Cancelar
                </Button>
                {splitMode === 'single' ? (
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={handleSplitBill}
                    disabled={!hasSelectedItems || processing}
                    data-testid="split-modal-confirm"
                  >
                    {processing ? 'Procesando...' : 'Crear Nueva Cuenta'}
                  </Button>
                ) : (
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={handleMultiSplitBill}
                    disabled={processing}
                    data-testid="split-modal-confirm-multi"
                  >
                    {processing ? 'Procesando...' : `Dividir en ${numSplitBills} Cuentas`}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Product Notes Modal (Quick Notes) */}
      {showNotesModal && notesProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="notes-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-orange-500 text-white px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-6 h-6" />
                  <div>
                    <h3 className="font-bold text-lg">{notesProduct.name}</h3>
                    <p className="text-orange-100 text-sm">${notesProduct.price?.toFixed(2)}</p>
                    {notesProduct.description && (
                      <p className="text-orange-200 text-xs mt-1" dangerouslySetInnerHTML={{ __html: '→ ' + parseMarkdown(notesProduct.description) }} />
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowNotesModal(false);
                    setNotesProduct(null);
                    setProductNotes('');
                    setSelectedQuickNotes([]);
                  }}
                  className="p-1 hover:bg-white/20 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Quick Notes */}
            <div className="p-4 border-b">
              <p className="text-sm font-medium text-gray-700 mb-3">Notas rápidas:</p>
              <div className="flex flex-wrap gap-2">
                {quickNotes.map(note => (
                  <button
                    key={note.id}
                    onClick={() => toggleQuickNote(note.label)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      selectedQuickNotes.includes(note.label)
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`quick-note-${note.id}`}
                  >
                    {selectedQuickNotes.includes(note.label) && <Check className="w-3 h-3 inline mr-1" />}
                    {note.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Custom Note */}
            <div className="p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Nota personalizada:</p>
              <input
                type="text"
                value={productNotes}
                onChange={(e) => setProductNotes(e.target.value)}
                placeholder="Ej: Sin mayonesa, con mucho limón..."
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="custom-note-input"
              />
              
              {/* Preview */}
              {(selectedQuickNotes.length > 0 || productNotes) && (
                <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-xs text-orange-600 mb-1">Vista previa:</p>
                  <p className="font-medium text-orange-800">
                    {notesProduct.name}: {[...selectedQuickNotes, productNotes].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={addToCartQuick}
                data-testid="add-without-notes-btn"
              >
                Sin notas
              </Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                onClick={addToCartWithNotes}
                data-testid="add-with-notes-btn"
              >
                <Check className="w-4 h-4 mr-1" />
                Agregar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Selectable Ingredients Modal */}
      {ingredientModalOpen && ingredientProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="ingredient-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-purple-600 text-white px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Layers className="w-6 h-6" />
                  <div>
                    <h3 className="font-bold text-lg">{ingredientProduct.name}</h3>
                    <p className="text-purple-200 text-sm">Selecciona los ingredientes</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setIngredientModalOpen(false);
                    setIngredientProduct(null);
                    setSelectedIngredients({});
                  }}
                  className="p-1 hover:bg-white/20 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Info */}
            <div className="px-5 py-3 bg-purple-50 border-b">
              <p className="text-sm text-purple-700">
                Peso total: <strong>{ingredientProduct.total_weight_grams || 0}g</strong> - 
                Se distribuirá entre los ingredientes seleccionados
              </p>
            </div>
            
            {/* Ingredients List */}
            <div className="p-5 max-h-[50vh] overflow-y-auto">
              <div className="space-y-2">
                {ingredientProduct.selectable_ingredients?.map((ing) => {
                  const isSelected = selectedIngredients[ing.processed_id] || false;
                  const selectedCount = Object.values(selectedIngredients).filter(Boolean).length;
                  const weightPerSelected = selectedCount > 0 
                    ? Math.round((ingredientProduct.total_weight_grams || 0) / selectedCount)
                    : 0;
                  
                  return (
                    <button
                      key={ing.processed_id}
                      onClick={() => setSelectedIngredients(prev => ({
                        ...prev,
                        [ing.processed_id]: !prev[ing.processed_id]
                      }))}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                        isSelected 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      data-testid={`ingredient-${ing.processed_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <span className={`font-medium ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                          {ing.name}
                        </span>
                      </div>
                      {isSelected && (
                        <span className="text-sm bg-purple-200 text-purple-700 px-2 py-1 rounded-full">
                          ~{weightPerSelected}g
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Preview */}
              {Object.values(selectedIngredients).some(Boolean) && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
                  <p className="text-xs text-gray-500 mb-2">Tu pedido:</p>
                  <p className="font-medium">
                    {ingredientProduct.name} con: {' '}
                    <span className="text-purple-600">
                      {ingredientProduct.selectable_ingredients
                        ?.filter(ing => selectedIngredients[ing.processed_id])
                        .map(ing => ing.name)
                        .join(', ')}
                    </span>
                  </p>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setIngredientModalOpen(false);
                  setIngredientProduct(null);
                  setSelectedIngredients({});
                }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                onClick={confirmIngredientSelection}
                disabled={!Object.values(selectedIngredients).some(Boolean)}
                data-testid="ingredient-confirm-btn"
              >
                Agregar al Pedido
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Custom/Special Product Modal */}
      {showCustomProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="custom-product-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-purple-600 text-white p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Star className="w-5 h-5" />
                Producto Personalizado
              </h2>
              <button onClick={() => setShowCustomProductModal(false)} className="hover:bg-purple-700 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Nombre del Producto *</Label>
                  <Input
                    value={customProductName}
                    onChange={(e) => setCustomProductName(e.target.value)}
                    placeholder="Ej: Ceviche Especial"
                    data-testid="custom-product-name"
                  />
                </div>
                <div>
                  <Label>Precio de Venta *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="0.01"
                      value={customProductPrice}
                      onChange={(e) => setCustomProductPrice(e.target.value)}
                      placeholder="0.00"
                      className="pl-9"
                      data-testid="custom-product-price"
                    />
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <Label>Notas (opcional)</Label>
                <Input
                  value={customProductNotes}
                  onChange={(e) => setCustomProductNotes(e.target.value)}
                  placeholder="Descripción o notas especiales"
                />
              </div>
              
              {/* Add Ingredient */}
              <div className="border rounded-lg p-4 mb-4 bg-gray-50">
                <Label className="text-sm font-medium mb-2 block">Agregar Ingredientes (Insumos/Elaborados)</Label>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-5">
                    <Input
                      placeholder="Buscar ingrediente..."
                      value={ingredientSearch}
                      onChange={(e) => {
                        setIngredientSearch(e.target.value);
                        setSelectedIngredient(null);
                      }}
                      data-testid="ingredient-search"
                    />
                    {ingredientSearch && filteredIngredients.length > 0 && !selectedIngredient && (
                      <div className="absolute z-10 mt-1 bg-white border rounded shadow-lg max-h-40 overflow-auto w-64">
                        {filteredIngredients.slice(0, 10).map(ing => (
                          <button
                            key={`${ing.type}-${ing.id}`}
                            onClick={() => {
                              setSelectedIngredient(ing);
                              setIngredientSearch(ing.name);
                              setIngredientUnit(ing.unit === 'kg' ? 'gr' : ing.unit);
                            }}
                            className="w-full text-left p-2 hover:bg-purple-50 text-sm border-b flex justify-between"
                          >
                            <span>
                              {ing.name}
                              <span className="text-xs text-purple-500 ml-1">
                                ({ing.type === 'processed' ? 'Elaborado' : 'Insumo'})
                              </span>
                            </span>
                            <span className="text-xs text-gray-500">${ing.cost_per_unit?.toFixed(2)}/{ing.unit}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Cantidad"
                      value={ingredientQuantity}
                      onChange={(e) => setIngredientQuantity(e.target.value)}
                      data-testid="ingredient-quantity"
                    />
                  </div>
                  <div className="col-span-2">
                    <select
                      value={ingredientUnit}
                      onChange={(e) => setIngredientUnit(e.target.value)}
                      className="w-full h-10 border rounded px-2 text-sm"
                    >
                      <option value="gr">gr</option>
                      <option value="kg">kg</option>
                      <option value="lt">lt</option>
                      <option value="ml">ml</option>
                      <option value="pz">pz</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Button
                      type="button"
                      onClick={addIngredientToCustomProduct}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      data-testid="add-ingredient-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Ingredients List */}
              <div className="border rounded-lg overflow-hidden mb-4">
                <div className="bg-gray-100 p-2 font-medium text-sm flex justify-between">
                  <span>Ingredientes Seleccionados</span>
                  <span>Costo</span>
                </div>
                {customProductIngredients.length === 0 ? (
                  <div className="p-4 text-center text-gray-400 text-sm">
                    Agrega ingredientes para calcular el costo
                  </div>
                ) : (
                  <div className="divide-y">
                    {customProductIngredients.map((ing, idx) => {
                      const qty = ing.unit === 'gr' ? ing.quantity / 1000 : ing.quantity;
                      const cost = qty * ing.cost_per_unit;
                      return (
                        <div key={`${ing.id}-${idx}`} className="p-2 flex justify-between items-center text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => removeIngredientFromCustomProduct(ing.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <span className={ing.type === 'processed' ? 'text-purple-600' : ''}>
                              {ing.name}
                            </span>
                            <span className="text-gray-500">
                              {ing.quantity} {ing.unit}
                            </span>
                          </div>
                          <span className="font-medium">${cost.toFixed(2)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Cost Summary */}
              {customProductIngredients.length > 0 && customProductPrice && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-xs text-gray-500">Costo Total</div>
                      <div className="text-lg font-bold text-red-600">
                        ${calculateCustomProductCost().toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Precio Venta</div>
                      <div className="text-lg font-bold text-gray-800">
                        ${parseFloat(customProductPrice || 0).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Ganancia</div>
                      <div className={`text-lg font-bold ${(parseFloat(customProductPrice || 0) - calculateCustomProductCost()) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${(parseFloat(customProductPrice || 0) - calculateCustomProductCost()).toFixed(2)}
                        <span className="text-xs ml-1">
                          ({((parseFloat(customProductPrice || 0) - calculateCustomProductCost()) / parseFloat(customProductPrice || 1) * 100).toFixed(0)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Save to Menu Option */}
              <div className="flex items-center gap-2 mt-4 p-3 bg-yellow-50 rounded-lg">
                <input
                  type="checkbox"
                  id="saveToMenu"
                  checked={saveToMenu}
                  onChange={(e) => setSaveToMenu(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="saveToMenu" className="text-sm flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Guardar en catálogo "Productos Especiales" (para vender después)
                </label>
              </div>
              
              <div className="flex items-start gap-2 mt-2 text-xs text-gray-500">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Los ingredientes se descontarán automáticamente del inventario al completar la venta.</span>
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t p-4 flex justify-end gap-2 bg-gray-50">
              <Button variant="outline" onClick={() => setShowCustomProductModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={createAndAddCustomProduct}
                disabled={creatingCustomProduct || !customProductName || !customProductPrice}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="create-custom-product-btn"
              >
                {creatingCustomProduct ? 'Creando...' : 'Crear y Agregar al Carrito'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Void/Cancel Order Modal */}
      {/* Discount Modal for Existing Orders */}
      <DiscountModal
        isOpen={discountModalOpen}
        subtotal={discountOrderSubtotal}
        discountAmount={discountAmount}
        setDiscountAmount={setDiscountAmount}
        discountReason={discountReason}
        setDiscountReason={setDiscountReason}
        onConfirm={applyDiscountToOrder}
        onClose={() => setDiscountModalOpen(false)}
        processing={processing}
      />

      {/* Change Table Modal */}
      <ChangeTableModal
        isOpen={changeTableModalOpen}
        currentTableNumber={changeTableCurrentNumber}
        tables={tables.filter(t => t.status === 'available')}
        getTableStatus={(t) => t.status === 'available' ? 'available' : 'occupied'}
        selectedNewTableId={changeTableNewId}
        setSelectedNewTableId={setChangeTableNewId}
        onConfirm={confirmChangeTable}
        onClose={() => setChangeTableModalOpen(false)}
        processing={processing}
      />

      {/* Edit Customer Info Modal */}
      <EditCustomerModal
        isOpen={editCustomerModalOpen && !!editCustomerOrder}
        customerName={editCustomerName}
        setCustomerName={setEditCustomerName}
        customerPhone={editCustomerPhone}
        setCustomerPhone={setEditCustomerPhone}
        editReason={editCustomerReason}
        setEditReason={setEditCustomerReason}
        onConfirm={confirmEditCustomer}
        onClose={() => setEditCustomerModalOpen(false)}
        processing={processing}
      />

      {/* Duplicate Customer Detection Modal */}
      <DuplicateCustomerModal
        isOpen={duplicateCustomerModalOpen}
        existingCustomer={duplicateCustomerData}
        onUseExisting={handleUseExistingCustomer}
        onCreateNew={handleCreateNewCustomerAnyway}
        onCancel={handleCancelDuplicateModal}
      />

      {/* Void Order Modal */}
      <VoidOrderModal
        isOpen={voidOrderModalOpen}
        orderNumber={voidOrderNumber}
        voidReason={voidReason}
        setVoidReason={setVoidReason}
        onConfirm={confirmVoidOrder}
        onClose={() => setVoidOrderModalOpen(false)}
        processing={processing}
      />

      {/* Close Table Modal - FUDO Style */}
      {closeTableModalOpen && closeTableData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gray-800 text-white p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {closeTableData.isOrder 
                  ? `COBRAR PEDIDO #${closeTableData.table.number}` 
                  : `CERRAR MESA ${closeTableData.table.number}`}
              </h2>
              <button 
                onClick={() => setCloseTableModalOpen(false)}
                className="p-1 hover:bg-gray-700 rounded"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex">
              {/* Left: Items */}
              <div className="w-1/2 border-r">
                <div className="bg-gray-700 text-white px-4 py-2 text-sm font-bold">
                  ADICIONES
                </div>
                <div className="max-h-60 overflow-auto">
                  {closeTableData.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center px-4 py-2 border-b text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-xs font-bold">
                          {item.quantity}
                        </span>
                        <span>{item.product_name}</span>
                      </div>
                      <span className="font-medium">${item.subtotal?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                
                {/* Totals */}
                <div className="border-t bg-gray-50">
                  <div className="flex justify-between px-4 py-2 text-sm">
                    <span>Subtotal:</span>
                    <span className="font-bold">${closeTableData.subtotal?.toFixed(2)}</span>
                  </div>
                  {closeTableData.discount > 0 && (
                    <div className="flex justify-between px-4 py-2 text-sm text-green-600">
                      <span>Descuento:</span>
                      <span className="font-bold">-${closeTableData.discount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between px-4 py-2 text-sm">
                    <span>Propina:</span>
                    <span className="font-bold">${(parseFloat(closeTipAmount) || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between px-4 py-3 bg-gray-800 text-white">
                    <span className="font-bold">Total:</span>
                    <span className="text-xl font-bold">
                      ${(closeTableData.total + (parseFloat(closeTipAmount) || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {/* Partial Payment Checkbox */}
                <div className="px-4 py-3 border-t">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={closePartialPayment}
                      onChange={(e) => setClosePartialPayment(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Cierre Parcial</span>
                  </label>
                </div>
              </div>

              {/* Right: Payment */}
              <div className="w-1/2">
                {/* Tip Section */}
                <div className="bg-gray-700 text-white px-4 py-2 text-sm font-bold flex items-center justify-between">
                  <span>PROPINA</span>
                  <button className="text-lg font-bold">+</button>
                </div>
                <div className="p-4 border-b">
                  <div className="flex items-center gap-2">
                    <select
                      value={closeTipMethod}
                      onChange={(e) => setCloseTipMethod(e.target.value)}
                      className="border rounded px-3 py-2 text-sm flex-1"
                    >
                      <option value="efectivo">Efectivo</option>
                      <option value="tarjeta">Tarjeta</option>
                      <option value="transferencia">Transferencia</option>
                    </select>
                    <div className="flex items-center border rounded">
                      <span className="px-2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={closeTipAmount}
                        onChange={(e) => setCloseTipAmount(e.target.value)}
                        placeholder="0"
                        className="w-20 py-2 pr-2 text-right outline-none"
                      />
                    </div>
                    <button 
                      onClick={() => setCloseTipAmount('')}
                      className="p-2 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Payment Section */}
                <div className="bg-green-600 px-4 py-2 text-sm font-bold text-white">
                  PAGO
                </div>
                <div className="p-4 border-b space-y-4">
                  <div className="flex items-center gap-2">
                    <select
                      value={closePaymentMethod}
                      onChange={(e) => setClosePaymentMethod(e.target.value)}
                      className="border rounded px-3 py-2 text-sm flex-1"
                      data-testid="close-payment-method"
                    >
                      <option value="efectivo">Efectivo</option>
                      <option value="tarjeta">Tarjeta</option>
                      <option value="transferencia">Transferencia</option>
                    </select>
                  </div>
                  
                  {/* Con cuánto paga - Campo principal */}
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-green-200">
                    <label className="block text-sm text-gray-600 mb-2 font-medium">
                      ¿Con cuánto paga el cliente?
                    </label>
                    <div className="flex items-center bg-white border-2 border-gray-300 rounded-lg focus-within:border-green-500 transition-colors">
                      <span className="px-4 text-2xl text-gray-400 font-bold">$</span>
                      <input
                        type="number"
                        value={closePaymentAmount}
                        onChange={(e) => setClosePaymentAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full py-4 pr-4 text-right text-3xl font-bold outline-none bg-transparent"
                        autoFocus
                        data-testid="close-payment-amount"
                      />
                    </div>
                    {/* Quick amount buttons */}
                    <div className="flex gap-2 mt-3">
                      {[50, 100, 200, 500].map(amount => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => setClosePaymentAmount(amount.toString())}
                          className="flex-1 py-2 bg-gray-100 hover:bg-green-100 rounded text-sm font-medium transition-colors"
                        >
                          ${amount}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setClosePaymentAmount((closeTableData.total + (parseFloat(closeTipAmount) || 0)).toFixed(2))}
                        className="flex-1 py-2 bg-green-100 hover:bg-green-200 rounded text-sm font-medium text-green-700 transition-colors"
                      >
                        Exacto
                      </button>
                    </div>
                  </div>
                </div>

                {/* Change - Más visible */}
                <div className={`flex justify-between items-center px-4 py-4 ${
                  (parseFloat(closePaymentAmount) || 0) >= (closeTableData.total + (parseFloat(closeTipAmount) || 0))
                    ? 'bg-green-100'
                    : 'bg-orange-100'
                }`}>
                  <span className="text-gray-700 font-medium">Cambio a entregar:</span>
                  <span className={`text-3xl font-bold ${
                    (parseFloat(closePaymentAmount) || 0) >= (closeTableData.total + (parseFloat(closeTipAmount) || 0))
                      ? 'text-green-700'
                      : 'text-orange-600'
                  }`}>
                    ${Math.max(0, (parseFloat(closePaymentAmount) || 0) - (closeTableData.total + (parseFloat(closeTipAmount) || 0))).toFixed(2)}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 p-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setCloseTableModalOpen(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={processCloseTable}
                    disabled={processing || (!closePartialPayment && (parseFloat(closePaymentAmount) || 0) < (closeTableData.total + (parseFloat(closeTipAmount) || 0)))}
                    className={`flex-1 text-white ${
                      (!closePartialPayment && (parseFloat(closePaymentAmount) || 0) < (closeTableData.total + (parseFloat(closeTipAmount) || 0)))
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    data-testid="close-order-btn"
                  >
                    {processing 
                      ? 'Procesando...' 
                      : (parseFloat(closePaymentAmount) || 0) < (closeTableData.total + (parseFloat(closeTipAmount) || 0)) && !closePartialPayment
                        ? 'Ingrese monto de pago'
                        : closeTableData.isOrder 
                          ? `Cobrar $${(closeTableData.total + (parseFloat(closeTipAmount) || 0)).toFixed(2)}`
                          : `Cerrar mesa $${(closeTableData.total + (parseFloat(closeTipAmount) || 0)).toFixed(2)}`
                    }
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loyalty Points Redemption Modal */}
      <RedeemModal
        isOpen={redeemModalOpen}
        redeemCode={redeemCode}
        setRedeemCode={setRedeemCode}
        redeemPin={redeemPin}
        setRedeemPin={setRedeemPin}
        pendingRedemptions={pendingRedemptions}
        onVerify={verifyRedemption}
        onClose={() => {
          setRedeemModalOpen(false);
          setRedeemCode('');
          setRedeemPin('');
        }}
        loading={redeemLoading}
      />

      {/* Loyalty Points Panel - Shows when customer has redeemable products */}
      <LoyaltyPanel
        isOpen={showLoyaltyPanel}
        customer={selectedCustomer}
        redeemableProducts={vipRedeemableProducts.length > 0 ? vipRedeemableProducts : selectedCustomer?.loyalty?.redeemable_products || []}
        onRedeemProduct={applyLoyaltyRedemption}
        onSendWhatsApp={() => {
          setShowLoyaltyPanel(false);
          const msg = encodeURIComponent(
            `¡Hola ${selectedCustomer.name}! 🐸\n\n` +
            `Tienes ${selectedCustomer.loyalty.points_available} puntos en Mr Frogs.\n` +
            `Equivalen a $${selectedCustomer.loyalty.points_value_pesos} pesos.\n\n` +
            `¡Ven a canjearlos por productos gratis!`
          );
          window.open(`https://wa.me/52${selectedCustomer.phone}?text=${msg}`, '_blank');
        }}
        onClose={() => setShowLoyaltyPanel(false)}
      />

      {/* VIP Customer Alert Modal */}
      <VIPAlertModal
        isOpen={vipAlertModalOpen}
        customer={vipAlertCustomer}
        redeemableProducts={vipRedeemableProducts}
        onRedeemProduct={(product) => {
          applyLoyaltyRedemption(product);
          setVipAlertModalOpen(false);
        }}
        onViewAllProducts={async () => {
          setVipAlertModalOpen(false);
          if (vipRedeemableProducts.length === 0 && selectedCustomer?.phone) {
            try {
              const res = await api.get(`/customer-portal/redeemable-products/${user.user_id}?phone=${selectedCustomer.phone}`);
              const products = res.data.products || [];
              setVipRedeemableProducts(products.filter(p => p.can_afford));
            } catch (error) {
              console.error('Error loading redeemable products:', error);
            }
          }
          setShowLoyaltyPanel(true);
        }}
        onClose={() => setVipAlertModalOpen(false)}
      />
    </div>
  );
};

export default POS;
